import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Parts from './Parts';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import api from '../services/api';
import './Shopping.css';

const Shopping = () => {
  const { user, updateUser } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('computer-store');
  
  // Hotel booking states
  const [hotels, setHotels] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [bookingNights, setBookingNights] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [accommodationTab, setAccommodationTab] = useState('hotels');
  
  // Bank states
  const [bankingData, setBankingData] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [activeService, setActiveService] = useState('overview');

  // Business states
  const [businessTypes, setBusinessTypes] = useState([]);
  const [userBusiness, setUserBusiness] = useState(null);
  const [selectedBusinessIndex, setSelectedBusinessIndex] = useState(0);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessError, setBusinesError] = useState(null);
  const [businessTab, setBusinessTab] = useState('create');
  const [selectedBusinessType, setSelectedBusinessType] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [foodDrinkData, setFoodDrinkData] = useState({ currentSupplies: 10 });
  const [supplyPurchase, setSupplyPurchase] = useState({ supplies: 0 });
  const [supplyChainStatus, setSupplyChainStatus] = useState(null);
  const [businessLinkRequests, setBusinessLinkRequests] = useState([]);
  const [outgoingLinkRequests, setOutgoingLinkRequests] = useState([]);

  // Business requirements
  const getBusinessRequirements = (businessType) => {
    const requirements = {
      farm: { xp: 1000, cost: 100000 },
      warehouse: { xp: 1250, cost: 125000 },
      factory: { xp: 1500, cost: 150000 },
      shop: { xp: 2000, cost: 200000 },
      fishing: { xp: 1000, cost: 100000 } // Same requirements as farm
    };
    return requirements[businessType] || { xp: 1000, cost: 100000 };
  };

  // Career states
  const [careerData, setCareerData] = useState({
    offices: [],
    userJob: null,
    workSession: null
  });
  const [careerLoading, setCareerLoading] = useState(false);
  const [careerError, setCareerError] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [streakLoading, setStreakLoading] = useState(false);

  // Load data when tabs are active
  useEffect(() => {
    if (activeTab === 'accommodation') {
      fetchHotelsAndCountries();
      fetchUserBookings();
    } else if (activeTab === 'bank') {
      fetchBankingData();
    } else if (activeTab === 'businesses') {
      fetchBusinessData();
      fetchSupplyChainStatus();
      fetchBusinessLinkRequests();
    } else if (activeTab === 'career') {
      fetchCareerData();
      fetchStreakData();
    }
  }, [activeTab]);


  // Fetch hotels when country filter changes
  useEffect(() => {
    if (activeTab === 'accommodation' && accommodationTab === 'hotels') {
      if (selectedCountry !== 'all') {
        fetchHotelsByCountry(selectedCountry);
      } else {
        fetchAllHotels();
      }
    }
  }, [selectedCountry, activeTab, accommodationTab]);

  // Banking functions
  const fetchBankingData = async () => {
    try {
      setBankLoading(true);
      setBankError(null);
      
      const response = await api.get('/bank/overview');
      const data = response.data;
      
      if (data.success) {
        setBankingData(data.data);
      } else {
        setBankError(data.message || 'Failed to load banking data');
      }
    } catch (err) {
      console.error('Error fetching banking data:', err);
      setBankError('Failed to load banking data');
    } finally {
      setBankLoading(false);
    }
  };

  const handleBankingAction = async (endpoint, requestData, successMessage) => {
    try {
      setBankLoading(true);
      
      // Handle overview endpoint specially - it should be a GET request
      if (endpoint === 'overview') {
        await fetchBankingData();
        return { success: true };
      }
      
      const response = await api.post(`/bank/${endpoint}`, requestData);
      
      const data = response.data;
      
      if (data.success) {
        addNotification({
          title: 'Banking Success',
          message: successMessage || data.message,
          type: 'success'
        });
        
        // Refresh banking data and user info
        await Promise.all([
          fetchBankingData(),
          updateUser()
        ]);
        
        return { success: true, data: data };
      } else {
        addNotification({
          title: 'Banking Error',
          message: data.message,
          type: 'error'
        });
        return { success: false, message: data.message };
      }
    } catch (err) {
      console.error('Banking action error:', err);
      addNotification({
        title: 'Banking Error',
        message: 'Network error occurred',
        type: 'error'
      });
      return { success: false, message: 'Network error' };
    } finally {
      setBankLoading(false);
    }
  };

  // Business functions
  const fetchBusinessData = async () => {
    try {
      setBusinessLoading(true);
      setBusinesError(null);
      
      // Fetch business types
      const typesResponse = await api.get('/business/types');
      if (typesResponse.data) {
        setBusinessTypes(typesResponse.data.businessTypes || []);
      }
      
      // Fetch user's businesses
      const userBusinessResponse = await api.get('/business/my-business');
      if (userBusinessResponse.data) {
        setUserBusiness(userBusinessResponse.data.businesses || []); // Now stores array of businesses
      }
      
      
      // Update survival data
      await updateSurvivalData();
      
    } catch (err) {
      console.error('Error fetching business data:', err);
      setBusinesError('Failed to load business data');
    } finally {
      setBusinessLoading(false);
    }
  };

  const updateSurvivalData = async () => {
    try {
      const response = await api.post('/business/survival/update');
      const data = response.data;
      
      if (data.success) {
        setFoodDrinkData({
          currentSupplies: data.foodDrink?.currentSupplies || 10
        });
      }
    } catch (err) {
      console.error('Error updating survival data:', err);
    }
  };

  const handleUnlinkBusiness = async (supplierBusinessId, supplierBusinessName, linkType) => {
    try {
      const confirmed = window.confirm(
        `Are you sure you want to unlink from "${supplierBusinessName}"?\n\n` +
        'This will remove the supply chain connection between your businesses.'
      );
      
      if (!confirmed) return;

      // Extract the actual ID from the business object
      let businessIdString;
      if (typeof supplierBusinessId === 'object' && supplierBusinessId !== null) {
        // If it's an object, it might be a populated business object
        businessIdString = supplierBusinessId._id || supplierBusinessId.id || supplierBusinessId;
      } else {
        // If it's already a string, use it directly
        businessIdString = supplierBusinessId;
      }

      const response = await api.post('/business/unlink', { supplierBusinessId: businessIdString });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '✅ Business Unlinked',
          message: `Successfully unlinked from ${supplierBusinessName}`,
          type: 'success'
        });

        // Refresh business data
        fetchBusinessData();
      } else {
        addNotification({
          title: '❌ Unlink Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error unlinking business:', error);
      addNotification({
        title: '❌ Unlink Error',
        message: 'Failed to unlink business. Please try again.',
        type: 'error'
      });
    }
  };

  const handleDeleteBusiness = async (businessId, businessName) => {
    if (!userBusiness || userBusiness.length === 0) {
      addNotification({
        title: '❌ Error',
        message: 'No business to delete',
        type: 'error'
      });
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete "${businessName}"?\n\n` +
      '⚠️ WARNING: This action cannot be undone!\n' +
      '• Your business will be permanently removed\n' +
      '• All supply chain connections will be broken\n' +
      '• Your business image will be deleted\n' +
      '• You will need to create a new business from scratch'
    );

    if (!confirmed) {
      return;
    }

    try {
      setBusinessLoading(true);
      
      const response = await api.delete('/business/delete', {
        data: { businessId: businessId }
      });

      const data = response.data;

      if (data.success) {
        // Create detailed success message
        let detailMessage = data.message;
        if (data.deletionDetails) {
          const details = data.deletionDetails;
          detailMessage += `\n\n📊 Deletion Summary:`;
          detailMessage += `\n• Business removed from database: ${details.businessDeleted ? '✅' : '❌'}`;
          detailMessage += `\n• Business image deleted: ${details.imageDeleted ? '✅' : '❌'}`;
          detailMessage += `\n• Supply chain connections removed: ${details.connectionsRemoved}`;
          if (details.imagePath) {
            detailMessage += `\n• Image file: ${details.imagePath}`;
          }
        }
        
        addNotification({
          title: '🗑️ Business Deleted',
          message: detailMessage,
          type: 'success'
        });
        
        // Reset selected business index if we're deleting the current one
        const currentBusinessId = userBusiness[selectedBusinessIndex]?._id;
        if (currentBusinessId === businessId) {
          setSelectedBusinessIndex(0); // Reset to first business
        } else if (selectedBusinessIndex > 0) {
          // If we deleted a business before the current selection, adjust the index
          const deletedIndex = userBusiness.findIndex(b => b._id === businessId);
          if (deletedIndex < selectedBusinessIndex) {
            setSelectedBusinessIndex(selectedBusinessIndex - 1);
          }
        }
        
        // Refresh all business data
        await fetchBusinessData();
        
        // Switch to create tab if no businesses left
        const refreshedBusiness = await api.get('/business/my-business');
        const businessData = refreshedBusiness.data;
        
        if (!businessData.businesses || businessData.businesses.length === 0) {
          setBusinessTab('create');
        }
      } else {
        addNotification({
          title: '❌ Deletion Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting business:', error);
      addNotification({
        title: '❌ Deletion Error',
        message: 'Failed to delete business. Please try again.',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleCreateBusiness = async () => {
    if (!selectedBusinessType || !businessName.trim()) {
      addNotification({
        title: 'Validation Error',
        message: 'Please select a business type and enter a business name',
        type: 'error'
      });
      return;
    }

    // Check business requirements
    const requirements = getBusinessRequirements(selectedBusinessType);
    

    // Check XP requirement
    if (user.xp < requirements.xp) {
      addNotification({
        title: 'Insufficient XP',
        message: `You need at least ${requirements.xp.toLocaleString()} XP to create a ${selectedBusinessType}`,
        type: 'error'
      });
      return;
    }

    // Check coin requirement
    if (user.wrCoins < requirements.cost) {
      addNotification({
        title: 'Insufficient Funds',
        message: `You need ${requirements.cost.toLocaleString()} WR coins to create a ${selectedBusinessType}`,
        type: 'error'
      });
      return;
    }

    try {
      setBusinessLoading(true);
      
      const response = await api.post('/business/create', {
        businessType: selectedBusinessType,
        businessName: businessName.trim(),
        description: businessDescription.trim()
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '🏗️ Business Built!',
          message: data.message + ' Now place it on the map!',
          type: 'success'
        });
        
        // Update user balance
        updateUser({ 
          wrCoins: user.wrCoins - 700000,
          businessId: data.business.id 
        });
        
        // Refresh business data
        await fetchBusinessData();
        
        // Reset form
        setSelectedBusinessType('');
        setBusinessName('');
        setBusinessDescription('');
        setBusinessTab('manage');
        
        // Navigate to map for business placement
        setTimeout(() => {
          navigate('/hack-the-planet');
        }, 2000);
      } else {
        addNotification({
          title: 'Creation Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error creating business:', error);
      addNotification({
        title: 'Error',
        message: 'Failed to create business',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  const handlePurchaseSupplies = async (businessId) => {
    const suppliesAmount = parseInt(supplyPurchase.supplies) || 0;
    
    if (suppliesAmount === 0) {
      addNotification({
        title: 'Invalid Purchase',
        message: 'Please select at least 1 Food/Drink supply',
        type: 'error'
      });
      return;
    }

    const totalCost = suppliesAmount;
    if (user.wrCoins < totalCost) {
      addNotification({
        title: 'Insufficient Funds',
        message: `You need ${totalCost} WR coins for this purchase`,
        type: 'error'
      });
      return;
    }

    try {
      setBusinessLoading(true);
      
      const response = await api.post('/business/purchase-supplies', {
        businessId: businessId,
        suppliesAmount: suppliesAmount
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: 'Supplies Purchased!',
          message: data.message,
          type: 'success'
        });
        
        // Update user balance and foodDrink supplies
        updateUser({ wrCoins: data.remaining });
        setFoodDrinkData(data.foodDrink);
        
        // Reset purchase form
        setSupplyPurchase({ supplies: 0 });
        
        // Refresh business data
        await fetchBusinessData();
      } else {
        addNotification({
          title: 'Purchase Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error purchasing supplies:', error);
      addNotification({
        title: 'Error',
        message: 'Failed to purchase supplies',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleRegenerateBusinessImage = async () => {
    try {
      setBusinessLoading(true);
      
      const response = await api.post('/business/regenerate-image');

      const data = response.data;

      if (data.success) {
        addNotification({
          title: 'Image Regenerated!',
          message: data.message,
          type: 'success'
        });
        
        // Update business image
        setUserBusiness(prev => ({
          ...prev,
          businessImageUrl: data.businessImageUrl
        }));
      } else {
        addNotification({
          title: 'Regeneration Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error regenerating business image:', error);
      addNotification({
        title: 'Error',
        message: 'Failed to regenerate business image',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  // Generate supplies for farms and fishing businesses
  const handleGenerateSupplies = async () => {
    if (!userBusiness || userBusiness.length === 0 || !['farm', 'fishing'].includes(userBusiness[selectedBusinessIndex]?.businessType)) {
      addNotification({
        title: '❌ Invalid Business Type',
        message: 'Only farms and fishing businesses can generate supplies',
        type: 'error'
      });
      return;
    }

    try {
      setBusinessLoading(true);
      
      const response = await api.post('/business/generate-supplies', {
        businessId: userBusiness[selectedBusinessIndex]?._id
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '✅ Supplies Generated!',
          message: data.message,
          type: 'success'
        });
        
        // Refresh business data to show updated supply levels
        await fetchBusinessData();
      } else {
        addNotification({
          title: '❌ Generation Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      // Only log errors in development mode to reduce console spam
      if (process.env.NODE_ENV === 'development') {
        console.error('Error generating supplies:', error);
      }
      
      // Check if it's a 400 error with specific message about capacity
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || '';
        
        if (errorMessage.includes('maximum capacity') || errorMessage.includes('Cannot produce more supplies')) {
          addNotification({
            title: '📦 Stock Full',
            message: 'Business is at maximum capacity. Cannot produce more supplies.',
            type: 'warning'
          });
        } else {
          // Other 400 errors (cooldown, not placed, etc.)
          addNotification({
            title: '⚠️ Cannot Generate',
            message: errorMessage || 'Cannot generate supplies at this time',
            type: 'warning'
          });
        }
      } else {
        addNotification({
          title: '❌ Generation Error',
          message: 'Failed to generate supplies',
          type: 'error'
        });
      }
    } finally {
      setBusinessLoading(false);
    }
  };

  // Purchase supplies from connected suppliers
  const handlePurchaseFromSupplier = async (supplierBusinessId) => {
    if (!userBusiness) {
      addNotification({
        title: '❌ No Business',
        message: 'You must own a business to purchase supplies',
        type: 'error'
      });
      return;
    }

    // Get the amount from the input field
    const amountInput = document.getElementById(`purchase-amount-${supplierBusinessId}`);
    const amount = parseInt(amountInput?.value) || 10;

    if (amount < 1 || amount > 100) {
      addNotification({
        title: '❌ Invalid Amount',
        message: 'Please enter a valid amount (1-100)',
        type: 'error'
      });
      return;
    }

    try {
      setBusinessLoading(true);
      
      const response = await api.post('/business/purchase-supply-chain-supplies', {
        supplierBusinessId: supplierBusinessId,
        supplyAmount: amount
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '✅ Supplies Purchased!',
          message: data.message,
          type: 'success'
        });
        
        // Update user balance
        updateUser({ wrCoins: user.wrCoins - data.purchase.cost });
        
        // Refresh business data to show updated supply levels
        await fetchBusinessData();
      } else {
        addNotification({
          title: '❌ Purchase Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error purchasing supplies from supplier:', error);
      addNotification({
        title: '❌ Purchase Error',
        message: 'Failed to purchase supplies from supplier',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  // Accept upstream pricing and activate supply generation
  const handleAcceptUpstreamPricing = async (upstreamSupplierId) => {
    if (!userBusiness || userBusiness.length === 0) {
      addNotification({
        title: '❌ No Business',
        message: 'You must own a business to accept pricing',
        type: 'error'
      });
      return;
    }

    // Get the current business
    const currentBusiness = userBusiness[selectedBusinessIndex] || userBusiness[0];

    try {
      setBusinessLoading(true);
      
      const response = await api.post('/business/accept-upstream-pricing', {
        businessId: currentBusiness._id,
        upstreamSupplierId: upstreamSupplierId
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '✅ Pricing Accepted!',
          message: data.message,
          type: 'success'
        });
        
        // Show supply chain status
        if (data.supplyChainStatus) {
          addNotification({
            title: data.supplyChainStatus.flowActive ? '🔥 Supply Flow Active!' : '⏳ Chain Status',
            message: data.supplyChainStatus.message,
            type: data.supplyChainStatus.flowActive ? 'success' : 'info'
          });
        }
        
        // Refresh business data to show updated pricing agreement
        await fetchBusinessData();
      } else {
        addNotification({
          title: '❌ Failed to Accept Pricing',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error accepting upstream pricing:', error);
      addNotification({
        title: '❌ Pricing Error',
        message: 'Failed to accept upstream pricing',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  // Fetch supply chain status
  const fetchSupplyChainStatus = async () => {
    try {
      const response = await api.get('/business/supply-chain-status');
      const data = response.data;

      if (data.success) {
        setSupplyChainStatus(data.data);
      } else {
        console.error('Failed to fetch supply chain status:', data.message);
      }
    } catch (error) {
      console.error('Error fetching supply chain status:', error);
    }
  };

  // Fetch pending business link requests
  const fetchBusinessLinkRequests = async () => {
    try {
      const response = await api.get('/business/link-requests');

      const data = response.data;

      if (data.success) {
        setBusinessLinkRequests(data.data?.incoming || []);
        setOutgoingLinkRequests(data.data?.outgoing || []);
      } else {
        console.error('Failed to fetch business link requests:', data.message);
      }
    } catch (error) {
      console.error('Error fetching business link requests:', error);
    }
  };

  // Cancel business link request
  const cancelBusinessLinkRequest = async (requestId) => {
    try {
      setBusinessLoading(true);
      
      const response = await api.post('/business/cancel-link-request', {
        requestId: requestId
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '🚫 Link Request Cancelled',
          message: data.message,
          type: 'success'
        });
        
        // Refresh business data and link requests
        await fetchBusinessData();
        await fetchBusinessLinkRequests();
      } else {
        addNotification({
          title: '❌ Cancel Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error cancelling business link request:', error);
      addNotification({
        title: '❌ Cancel Failed',
        message: 'Failed to cancel link request',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  // Accept business link request
  const acceptBusinessLinkRequest = async (requestId) => {
    try {
      setBusinessLoading(true);
      
      const response = await api.post('/business/respond-link-request', {
        requestId: requestId,
        action: 'accept'
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '✅ Link Request Accepted',
          message: data.message,
          type: 'success'
        });
        
        // Refresh business data and link requests
        await fetchBusinessData();
        await fetchBusinessLinkRequests();
      } else {
        addNotification({
          title: '❌ Accept Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error accepting business link request:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to accept link request. Please try again.',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  // Reject business link request
  const rejectBusinessLinkRequest = async (requestId) => {
    try {
      setBusinessLoading(true);
      
      const response = await api.post('/business/respond-link-request', {
        requestId: requestId,
        action: 'reject'
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '✅ Link Request Rejected',
          message: data.message,
          type: 'success'
        });
        
        // Refresh link requests
        await fetchBusinessLinkRequests();
      } else {
        addNotification({
          title: '❌ Reject Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error rejecting business link request:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to reject link request. Please try again.',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  // Helper function to update specific pricing
  const updateSpecificPrice = async (priceType, inputId) => {
    const priceInput = document.getElementById(inputId);
    const newPrice = parseInt(priceInput?.value);
    
    if (!newPrice || newPrice < 1 || newPrice > 1000) {
      addNotification({
        title: '❌ Invalid Price',
        message: 'Price must be between 1 and 1000 WR coins',
        type: 'error'
      });
      return;
    }
    
    try {
      setBusinessLoading(true);
      
      const currentBusiness = userBusiness?.[selectedBusinessIndex] || userBusiness?.[0];
      if (!currentBusiness) {
        throw new Error('No business selected');
      }
      
      const response = await api.post('/business/update-specific-pricing', {
        businessId: currentBusiness._id,
        priceType: priceType,
        newPrice: newPrice
      });
      
      const data = response.data;
      
      if (response.status === 200) {
        addNotification({
          title: '✅ Price Updated',
          message: data.message,
          type: 'success'
        });
        
        // Refresh business data to show updated pricing
        await fetchBusinessData();
      } else {
        addNotification({
          title: '❌ Update Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating specific pricing:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to update pricing. Please try again.',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  // Helper function to accept supplier pricing
  const acceptSupplierPrice = async (supplierId) => {
    try {
      setBusinessLoading(true);
      
      const currentBusiness = userBusiness?.[selectedBusinessIndex] || userBusiness?.[0];
      if (!currentBusiness) {
        throw new Error('No business selected');
      }
      
      const response = await api.post('/business/accept-supplier-pricing', {
        businessId: currentBusiness._id,
        supplierId: supplierId
      });
      
      const data = response.data;
      
      if (response.status === 200) {
        addNotification({
          title: '✅ Price Accepted',
          message: data.message,
          type: 'success'
        });
        
        // Refresh business data to show updated pricing
        await fetchBusinessData();
      } else {
        addNotification({
          title: '❌ Accept Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error accepting supplier pricing:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to accept pricing. Please try again.',
        type: 'error'
      });
    } finally {
      setBusinessLoading(false);
    }
  };

  const fetchHotelsAndCountries = async () => {
    try {
      setLoading(true);
      
      // Fetch countries
      const countriesResponse = await api.get('/hotel/hotels/countries');
      const countriesData = countriesResponse.data;
      
      if (countriesData.success) {
        setCountries(countriesData.countries);
      }
      
      // Fetch all hotels
      await fetchAllHotels();
      
    } catch (err) {
      console.error('Error fetching hotels and countries:', err);
      setError('Failed to load hotels');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllHotels = async () => {
    try {
      const response = await api.get('/hotel/hotels');
      const data = response.data;
      
      if (data.success) {
        setHotels(data.hotels);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch hotels');
      }
    } catch (err) {
      console.error('Error fetching hotels:', err);
      setError('Failed to load hotels');
    }
  };

  const fetchHotelsByCountry = async (country) => {
    try {
      const response = await api.get(`/hotel/hotels/country/${encodeURIComponent(country)}`);
      const data = response.data;
      
      if (data.success) {
        setHotels(data.hotels);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch hotels');
      }
    } catch (err) {
      console.error('Error fetching hotels by country:', err);
      setError('Failed to load hotels');
    }
  };

  const fetchUserBookings = async () => {
    try {
      if (!user?.token) return;
      
      const response = await api.get('/hotel/bookings/my-bookings');
      
      const data = response.data;
      
      if (data.success) {
        setUserBookings(data.bookings);
      }
    } catch (err) {
      console.error('Error fetching user bookings:', err);
    }
  };

  const handleBookHotel = async () => {
    if (!selectedHotel || bookingNights < 1 || bookingNights > 14) {
      addNotification({
        title: '❌ Invalid Booking',
        message: 'Please select valid number of nights (1-14)'
      });
      return;
    }

    try {
      setBookingLoading(true);
      
      const response = await api.post('/hotel/bookings/book', {
        hotelId: selectedHotel.hotelId,
        nights: bookingNights
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '🏨 Booking Confirmed!',
          message: `Successfully booked ${bookingNights} night(s) at ${selectedHotel.name}`
        });
        
        // Update user balance
        updateUser({ wrCoins: data.remainingBalance });
        
        // Refresh bookings and hotels
        await fetchUserBookings();
        await fetchAllHotels();
        
        // Close modal
        setShowBookingModal(false);
        setSelectedHotel(null);
        setBookingNights(1);
      } else {
        addNotification({
          title: '❌ Booking Failed',
          message: data.message
        });
      }
    } catch (err) {
      console.error('Error booking hotel:', err);
      addNotification({
        title: '❌ Booking Error',
        message: 'Failed to book hotel room'
      });
    } finally {
      setBookingLoading(false);
    }
  };

  const handleMoveToHotel = async (hotel) => {
    try {
      // Calculate a position near the hotel (not directly on top)
      // Add a small random offset (approximately 100-200 meters)
      const offsetDistance = 0.001 + Math.random() * 0.001; // ~100-200m in degrees
      const offsetAngle = Math.random() * 2 * Math.PI; // Random direction
      
      const nearHotelLat = hotel.coordinates.lat + (offsetDistance * Math.cos(offsetAngle));
      const nearHotelLng = hotel.coordinates.lng + (offsetDistance * Math.sin(offsetAngle));
      
      const response = await api.post('/treasure-hunt/computers/move', {
        lat: nearHotelLat,
        lng: nearHotelLng
      });

      if (response.status === 200) {
        addNotification({
          title: '🖥️ Computer Moving',
          message: `Computer is moving to ${hotel.name} location`
        });
      } else {
        addNotification({
          title: '❌ Movement Failed',
          message: response.data?.message || 'Failed to move computer'
        });
      }
    } catch (err) {
      console.error('Error moving computer:', err);
      addNotification({
        title: '❌ Movement Error',
        message: 'Failed to move computer to hotel'
      });
    }
  };

  const handleCheckIn = async (booking) => {
    try {
      const response = await api.post('/hotel/bookings/checkin', {
        bookingId: booking.bookingId
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '🏨 Checked In!',
          message: data.message
        });
        
        // Refresh bookings
        await fetchUserBookings();
      } else {
        addNotification({
          title: '❌ Check-in Failed',
          message: data.message
        });
      }
    } catch (err) {
      console.error('Error checking in:', err);
      addNotification({
        title: '❌ Check-in Error',
        message: 'Failed to check into hotel'
      });
    }
  };

  const handleCheckOut = async (booking) => {
    try {
      const response = await api.post('/hotel/bookings/checkout', {
        bookingId: booking.bookingId
      });

      const data = response.data;

      if (data.success) {
        addNotification({
          title: '🏨 Checked Out',
          message: data.message
        });
        
        // Refresh bookings
        await fetchUserBookings();
      } else {
        addNotification({
          title: '❌ Check-out Failed',
          message: data.message
        });
      }
    } catch (err) {
      console.error('Error checking out:', err);
      addNotification({
        title: '❌ Check-out Error',
        message: 'Failed to check out of hotel'
      });
    }
  };

  const filteredHotels = hotels.filter(hotel =>
    hotel.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    hotel.country.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStarRating = (rating) => {
    return '⭐'.repeat(rating);
  };

  // Bank component definitions
  const BankOverview = ({ user, bankingData, onRefresh }) => (
    <div className="bank-overview">
      <div className="overview-cards">
        <div className="overview-card">
          <h4>💰 Account Balance</h4>
          <div className="balance-display">{(user?.wrCoins || 0).toFixed(2)} WR</div>
          <p>Available for use</p>
        </div>
        
        <div className="overview-card">
          <h4>🏛️ Savings Account</h4>
          <div className="balance-display">
            {bankingData?.savingsAccount ? 
              `${(bankingData.savingsAccount.balance || 0).toFixed(2)} WR` : 
              'No Savings Account'
            }
          </div>
          <p>Interest rate: {bankingData?.savingsAccount?.interestRate || 5}% PAY</p>
        </div>
        
        <div className="overview-card">
          <h4>💳 Credit Available</h4>
          <div className="balance-display">
            {bankingData?.creditAccount ? 
              `${bankingData.creditAccount.availableCredit} WR` : 
              'No Credit Account'
            }
          </div>
          <p>Credit limit: {bankingData?.creditAccount?.creditLimit || 0} WR</p>
        </div>
        
        <div className="overview-card">
          <h4>📋 Active Loans</h4>
          <div className="balance-display">{bankingData?.loans?.length || 0}</div>
          <p>Total debt: {(bankingData?.loans?.reduce((sum, loan) => sum + loan.remainingBalance, 0) || 0).toFixed(2)} WR</p>
        </div>
        
        <div className="overview-card">
          <h4>🏠 Mortgages</h4>
          <div className="balance-display">{bankingData?.mortgages?.length || 0}</div>
          <p>Total debt: {(bankingData?.mortgages?.reduce((sum, m) => sum + m.remainingBalance, 0) || 0).toFixed(2)} WR</p>
        </div>
      </div>
      
      {bankingData?.recentTransactions?.length > 0 && (
        <div className="recent-transactions">
          <h4>Recent Transactions</h4>
          <div className="transactions-list">
            {bankingData.recentTransactions.slice(0, 5).map((transaction, index) => (
              <div key={index} className="transaction-item">
                <div className="transaction-desc">{transaction.description}</div>
                <div className="transaction-amount">{transaction.amount} WR</div>
                <div className="transaction-date">
                  {new Date(transaction.createdAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const BankSavings = ({ user, bankingData, onAction }) => {
    const [depositAmount, setDepositAmount] = useState('');
    const [withdrawAmount, setWithdrawAmount] = useState('');
    
    const savingsAccount = bankingData?.savingsAccount;
    const hasSavingsAccount = !!savingsAccount;
    
    const handleOpenSavings = () => {
      onAction('savings/open', {}, 'Savings account opened successfully!');
      setDepositAmount('');
      setWithdrawAmount('');
    };
    
    const handleDeposit = () => {
      const amount = parseFloat(depositAmount);
      if (amount > 0) {
        onAction('savings/deposit', { amount }, `${amount} WR deposited to savings`);
        setDepositAmount('');
      }
    };
    
    const handleWithdraw = () => {
      const amount = parseFloat(withdrawAmount);
      if (amount > 0) {
        onAction('savings/withdraw', { amount }, `${amount} WR withdrawn from savings`);
        setWithdrawAmount('');
      }
    };
    
    return (
      <div className="bank-savings">
        {!hasSavingsAccount ? (
          <div className="open-savings">
            <h4>🏛️ High-Interest Savings Account</h4>
            <div className="savings-offer">
              <p><strong>Interest Rate:</strong> 5% PAY (Per Annum Yearly)</p>
              <p><strong>Compounding:</strong> Daily interest calculation</p>
              <p><strong>Features:</strong> No minimum balance, unlimited deposits/withdrawals</p>
              <p><strong>Security:</strong> Funds backed by occult bank reserves</p>
            </div>
            <button className="service-button available-btn" onClick={handleOpenSavings}>
              Open Savings Account
            </button>
          </div>
        ) : (
          <div className="savings-account">
            <div className="savings-status">
              <div className="savings-info">
                <h4>🏛️ Savings Account Status</h4>
                <p><strong>Current Balance:</strong> {(savingsAccount.balance || 0).toFixed(2)} WR</p>
                <p><strong>Interest Rate:</strong> {savingsAccount.interestRate || 5}% PAY</p>
                <p><strong>Total Interest Earned:</strong> {(savingsAccount.totalInterestEarned || 0).toFixed(2)} WR</p>
                <p><strong>Last Interest Calculation:</strong> {savingsAccount.lastInterestCalculation ? new Date(savingsAccount.lastInterestCalculation).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
            
            <div className="savings-actions">
              <div className="savings-deposit">
                <h5>💰 Deposit Funds</h5>
                <div className="form-group">
                  <input
                    type="number"
                    placeholder="Amount to deposit"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    max={user?.wrCoins || 0}
                    min="1"
                    className="form-input"
                  />
                </div>
                <button 
                  className="service-button available-btn"
                  onClick={handleDeposit}
                  disabled={!depositAmount || parseFloat(depositAmount) > (user?.wrCoins || 0) || parseFloat(depositAmount) <= 0}
                >
                  Deposit to Savings
                </button>
              </div>
              
              <div className="savings-withdraw">
                <h5>💸 Withdraw Funds</h5>
                <div className="form-group">
                  <input
                    type="number"
                    placeholder="Amount to withdraw"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    max={savingsAccount.balance || 0}
                    min="1"
                    className="form-input"
                  />
                </div>
                <button 
                  className="service-button available-btn"
                  onClick={handleWithdraw}
                  disabled={!withdrawAmount || parseFloat(withdrawAmount) > (savingsAccount.balance || 0) || parseFloat(withdrawAmount) <= 0}
                >
                  Withdraw from Savings
                </button>
              </div>
            </div>
            
            <div className="savings-info-box">
              <h5>💡 Interest Information</h5>
              <p>Your savings earn 5% interest per year, calculated daily. Interest is automatically added to your balance every 24 hours.</p>
              <p>Daily Rate: {((savingsAccount.interestRate || 5) / 365).toFixed(4)}% per day</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const BankCredit = ({ user, bankingData, onAction }) => {
    const [creditAmount, setCreditAmount] = useState('');
    
    const creditAccount = bankingData?.creditAccount;
    const hasActiveCredit = !!creditAccount;
    
    const handleApplyCredit = () => {
      onAction('credit/apply', {}, 'Credit account application submitted');
    };
    
    const handleUseCredit = () => {
      const amount = parseFloat(creditAmount);
      if (amount > 0) {
        onAction('credit/advance', { amount }, `${amount} WR credit advance processed`);
        setCreditAmount('');
      }
    };
    
    return (
      <div className="bank-credit">
        {!hasActiveCredit ? (
          <div className="apply-credit">
            <h4>Apply for Credit Account</h4>
            <div className="credit-offer">
              <p><strong>Credit Limit:</strong> 1,000 WR</p>
              <p><strong>Interest Rate:</strong> 16% per month</p>
              <p><strong>Minimum Payment:</strong> 5% of balance</p>
              <p><strong>Restriction:</strong> One credit account per user until fully paid off</p>
            </div>
            <button className="service-button available-btn" onClick={handleApplyCredit}>
              Apply for Credit
            </button>
          </div>
        ) : (
          <div className="credit-account">
            <div className="credit-status">
              <div className="credit-info">
                <h4>💳 Credit Account Status</h4>
                <p><strong>Credit Limit:</strong> {creditAccount.creditLimit} WR</p>
                <p><strong>Available Credit:</strong> {creditAccount.availableCredit} WR</p>
                <p><strong>Current Balance:</strong> {creditAccount.currentBalance} WR</p>
                <p><strong>Minimum Payment:</strong> {(creditAccount.minimumPayment || 0).toFixed(2)} WR</p>
                {creditAccount.nextPaymentDue && (
                  <p><strong>Next Payment Due:</strong> {new Date(creditAccount.nextPaymentDue).toLocaleDateString()}</p>
                )}
              </div>
            </div>
            
            <div className="credit-advance">
              <h5>Use Credit</h5>
              <div className="form-group">
                <input
                  type="number"
                  placeholder="Amount to advance"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  max={creditAccount.availableCredit}
                  min="1"
                  className="form-input"
                />
              </div>
              <button 
                className="service-button available-btn"
                onClick={handleUseCredit}
                disabled={!creditAmount || parseFloat(creditAmount) > creditAccount.availableCredit}
              >
                Advance Credit
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const BankLoans = ({ user, bankingData, onAction }) => {
    const [loanAmount, setLoanAmount] = useState('');
    const [loanTerm, setLoanTerm] = useState('12');
    
    const loans = bankingData?.loans || [];
    const hasActiveLoan = loans.some(loan => loan.status === 'active');
    
    const handleApplyLoan = () => {
      const amount = parseFloat(loanAmount);
      const termMonths = parseInt(loanTerm);
      if (amount > 0 && termMonths > 0) {
        onAction('loan/apply', { amount, termMonths }, `Loan application for ${amount} WR submitted`);
        setLoanAmount('');
      }
    };
    
    return (
      <div className="bank-loans">
        {!hasActiveLoan ? (
          <div className="apply-loan">
            <h4>Apply for Loan</h4>
            <div className="loan-offer">
              <p><strong>Maximum Amount:</strong> 10,000 WR</p>
              <p><strong>Interest Rate:</strong> 12% APR per month</p>
              <p><strong>Terms:</strong> 6-60 months</p>
              <p><strong>Restriction:</strong> One loan per user until fully paid off</p>
            </div>
            
            <div className="loan-form">
              <div className="form-group">
                <label>Loan Amount (1 - 10,000 WR):</label>
                <input
                  type="number"
                  placeholder="Amount"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  max="10000"
                  min="1"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Term (months):</label>
                <select 
                  value={loanTerm} 
                  onChange={(e) => setLoanTerm(e.target.value)}
                  className="form-select"
                >
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                  <option value="24">24 months</option>
                  <option value="36">36 months</option>
                  <option value="48">48 months</option>
                  <option value="60">60 months</option>
                </select>
              </div>
              <button 
                className="service-button available-btn"
                onClick={handleApplyLoan}
                disabled={!loanAmount || parseFloat(loanAmount) > 10000}
              >
                Apply for Loan
              </button>
            </div>
          </div>
        ) : (
          <div className="loan-restriction">
            <h4>❌ Loan Application Unavailable</h4>
            <p>You already have an active loan. Please pay off your current loan completely before applying for a new one.</p>
          </div>
        )}
        
        {loans.length > 0 && (
          <div className="active-loans">
            <h4>Active Loans</h4>
            {loans.map((loan, index) => (
              <div key={index} className="loan-item">
                <div className="loan-info">
                  <p><strong>Original Amount:</strong> {loan.loanAmount} WR</p>
                  <p><strong>Remaining Balance:</strong> {loan.remainingBalance} WR</p>
                  <p><strong>Monthly Payment:</strong> {loan.monthlyPayment} WR</p>
                  <p><strong>Payments Remaining:</strong> {loan.paymentsRemaining}</p>
                  <p><strong>Next Payment:</strong> {new Date(loan.nextPaymentDue).toLocaleDateString()}</p>
                  <p><strong>Status:</strong> {loan.status}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const BankMortgage = ({ user, bankingData, onAction }) => {
    const [mortgageAmount, setMortgageAmount] = useState('');
    const [mortgageTerm, setMortgageTerm] = useState('20');
    const [showPayoffConfirm, setShowPayoffConfirm] = useState(null);
    
    const mortgages = bankingData?.mortgages || [];
    const hasActiveMortgage = mortgages.some(mortgage => mortgage.status === 'active');
    
    const handleApplyMortgage = () => {
      const amount = parseFloat(mortgageAmount);
      const termYears = parseInt(mortgageTerm);
      if (amount >= 100000 && amount <= 600000 && termYears > 0) {
        onAction('mortgage/apply', { amount, termYears }, `Mortgage application for ${amount} WR submitted`);
        setMortgageAmount('');
      }
    };

    const calculatePayoffPenalty = (originalAmount) => {
      return Math.floor(originalAmount * 0.1); // 10% of original amount
    };

    const handlePayoffMortgage = (mortgage) => {
      const penalty = calculatePayoffPenalty(mortgage.mortgageAmount);
      const totalPayoff = mortgage.remainingBalance + penalty;
      
      if (user.wrCoins < totalPayoff) {
        alert(`Insufficient funds! You need ${totalPayoff} WR (${mortgage.remainingBalance} WR remaining + ${penalty} WR penalty)`);
        return;
      }
      
      // Call the payoff API
      api.post(`/bank/mortgage/${mortgage._id}/payoff`)
      .then(response => {
        const data = response.data;
        if (data.success) {
          alert(data.message);
          // Refresh banking data
          onAction('overview', {}, '');
        } else {
          alert(`Error: ${data.message}`);
        }
      })
      .catch(error => {
        console.error('Error paying off mortgage:', error);
        alert('Failed to process mortgage payoff');
      });
      
      setShowPayoffConfirm(null);
    };
    
    return (
      <div className="bank-mortgage">
        {!hasActiveMortgage ? (
          <div className="apply-mortgage">
            <h4>Apply for Mortgage</h4>
            <div className="mortgage-offer">
              <p><strong>Amount Range:</strong> 100,000 - 600,000 WR</p>
              <p><strong>Interest Rate:</strong> 4% monthly</p>
              <p><strong>Terms:</strong> 18, 20, 24, 28, or 30 years</p>
              <p><strong>Restriction:</strong> One mortgage per user until fully paid off</p>
            </div>
            
            <div className="mortgage-form">
              <div className="form-group">
                <label>Mortgage Amount (100,000 - 600,000 WR):</label>
                <input
                  type="number"
                  placeholder="Amount"
                  value={mortgageAmount}
                  onChange={(e) => setMortgageAmount(e.target.value)}
                  max="600000"
                  min="100000"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Term (years):</label>
                <select 
                  value={mortgageTerm} 
                  onChange={(e) => setMortgageTerm(e.target.value)}
                  className="form-select"
                >
                  <option value="18">18 years</option>
                  <option value="20">20 years</option>
                  <option value="24">24 years</option>
                  <option value="28">28 years</option>
                  <option value="30">30 years</option>
                </select>
              </div>
              <button 
                className="service-button available-btn"
                onClick={handleApplyMortgage}
                disabled={!mortgageAmount || parseFloat(mortgageAmount) < 100000 || parseFloat(mortgageAmount) > 600000}
              >
                Apply for Mortgage
              </button>
            </div>
          </div>
        ) : (
          <div className="mortgage-restriction">
            <h4>❌ Mortgage Application Unavailable</h4>
            <p>You already have an active mortgage. Please pay off your current mortgage completely before applying for a new one.</p>
          </div>
        )}
        
        {mortgages.length > 0 && (
          <div className="active-mortgages">
            <h4>Active Mortgages</h4>
            {mortgages.map((mortgage, index) => {
              const penalty = calculatePayoffPenalty(mortgage.mortgageAmount);
              const totalPayoff = mortgage.remainingBalance + penalty;
              const canAfford = user.wrCoins >= totalPayoff;
              
              return (
                <div key={index} className="mortgage-item">
                  <div className="mortgage-info">
                    <p><strong>Original Amount:</strong> {mortgage.mortgageAmount?.toLocaleString()} WR</p>
                    <p><strong>Remaining Balance:</strong> {mortgage.remainingBalance?.toLocaleString()} WR</p>
                    <p><strong>Monthly Payment:</strong> {mortgage.monthlyPayment?.toLocaleString()} WR</p>
                    <p><strong>Term:</strong> {mortgage.termYears} years</p>
                    <p><strong>Payments Remaining:</strong> {mortgage.paymentsRemaining}</p>
                    <p><strong>Next Payment:</strong> {new Date(mortgage.nextPaymentDue).toLocaleDateString()}</p>
                    <p><strong>Status:</strong> {mortgage.status}</p>
                  </div>
                  
                  {mortgage.status === 'active' && (
                    <div className="mortgage-actions">
                      <div className="payoff-section">
                        <h5>💰 Early Payoff Option</h5>
                        <div className="payoff-details">
                          <p><strong>Remaining Balance:</strong> {mortgage.remainingBalance?.toLocaleString()} WR</p>
                          <p><strong>Early Payoff Penalty:</strong> {penalty.toLocaleString()} WR (10% of original loan)</p>
                          <p><strong>Total Payoff Amount:</strong> {totalPayoff.toLocaleString()} WR</p>
                          <p className="penalty-note">💡 Penalty goes to Occult Bank administrative fees</p>
                        </div>
                        
                        {showPayoffConfirm === mortgage._id ? (
                          <div className="payoff-confirm">
                            <p><strong>⚠️ Are you sure you want to pay off this mortgage?</strong></p>
                            <p>Total cost: {totalPayoff.toLocaleString()} WR</p>
                            <div className="confirm-buttons">
                              <button 
                                className="service-button danger-btn"
                                onClick={() => handlePayoffMortgage(mortgage)}
                                disabled={!canAfford}
                              >
                                {canAfford ? 'Confirm Payoff' : 'Insufficient Funds'}
                              </button>
                              <button 
                                className="service-button"
                                onClick={() => setShowPayoffConfirm(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button 
                            className="service-button payoff-btn"
                            onClick={() => setShowPayoffConfirm(mortgage._id)}
                            disabled={!canAfford}
                          >
                            {canAfford ? `💰 Pay Off Early (${totalPayoff.toLocaleString()} WR)` : 'Insufficient Funds'}
                          </button>
                        )}
                        
                        {!canAfford && (
                          <p className="insufficient-funds">
                            Need {(totalPayoff - user.wrCoins).toLocaleString()} more WR
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const BankTransfer = ({ user, onAction }) => {
    const [transferTo, setTransferTo] = useState('');
    const [transferAmount, setTransferAmount] = useState('');
    const [transferDescription, setTransferDescription] = useState('');
    
    const handleTransfer = () => {
      const amount = parseFloat(transferAmount);
      if (amount > 0 && transferTo.trim()) {
        onAction('transfer', {
          toUsername: transferTo.trim(),
          amount,
          description: transferDescription
        }, `Transfer of ${amount} WR sent to ${transferTo}`);
        setTransferTo('');
        setTransferAmount('');
        setTransferDescription('');
      }
    };
    
    return (
      <div className="bank-transfer">
        <h4>Wire Transfer</h4>
        <div className="transfer-info">
          <p><strong>Transfer Fee:</strong> 10 WR per transaction</p>
          <p><strong>Your Balance:</strong> {(user?.wrCoins || 0).toFixed(2)} WR</p>
        </div>
        
        <div className="transfer-form">
          <div className="form-group">
            <label>Recipient Username:</label>
            <input
              type="text"
              placeholder="Enter username"
              value={transferTo}
              onChange={(e) => setTransferTo(e.target.value)}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Amount (+ 10 WR fee):</label>
            <input
              type="number"
              placeholder="Amount to transfer"
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
              min="1"
              max={Math.max(0, (user?.wrCoins || 0) - 10)}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Description (optional):</label>
            <input
              type="text"
              placeholder="Transfer description"
              value={transferDescription}
              onChange={(e) => setTransferDescription(e.target.value)}
              className="form-input"
            />
          </div>
          <div className="transfer-summary">
            <p><strong>Total Cost:</strong> {transferAmount ? (parseFloat(transferAmount) + 10).toFixed(2) : '0.00'} WR</p>
          </div>
          <button 
            className="service-button available-btn"
            onClick={handleTransfer}
            disabled={!transferTo || !transferAmount || (parseFloat(transferAmount) + 10) > (user?.wrCoins || 0)}
          >
            Send Transfer
          </button>
        </div>
      </div>
    );
  };

  // House Viewing Modal Component
  const HouseViewModal = ({ house, isOpen, onClose }) => {
    if (!isOpen || !house) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-content house-view-modal">
          <div className="modal-header">
            <h3>🏠 House Details</h3>
            <button className="close-button" onClick={onClose}>✕</button>
          </div>
          
          <div className="modal-body">
            <div className="house-view-content">
              {house.houseImageUrl && (
                <div className="house-image-container">
                  <img 
                    src={house.houseImageUrl} 
                    alt={`${house.bedrooms} bedroom house by ${house.ownerUsername}`}
                    className="house-view-image"
                  />
                </div>
              )}
              
              <div className="house-details">
                <div className="house-owner">
                  <h4>Owner: {house.ownerUsername}</h4>
                </div>
                
                <div className="house-specs">
                  <div className="spec-item">
                    <span className="spec-label">🛏️ Bedrooms:</span>
                    <span className="spec-value">{house.bedrooms}</span>
                  </div>
                  
                  {house.hasLounge && (
                    <div className="spec-item">
                      <span className="spec-label">🛋️ Lounge:</span>
                      <span className="spec-value">Yes</span>
                    </div>
                  )}
                  
                  {house.hasKitchen && (
                    <div className="spec-item">
                      <span className="spec-label">🍳 Kitchen:</span>
                      <span className="spec-value">Yes</span>
                    </div>
                  )}
                  
                  {house.hasDiningRoom && (
                    <div className="spec-item">
                      <span className="spec-label">🍽️ Dining Room:</span>
                      <span className="spec-value">Yes</span>
                    </div>
                  )}
                  
                  {house.hasGarden && (
                    <div className="spec-item">
                      <span className="spec-label">🌺 Garden:</span>
                      <span className="spec-value">Yes</span>
                    </div>
                  )}
                  
                  <div className="spec-item">
                    <span className="spec-label">🏗️ Build Cost:</span>
                    <span className="spec-value">{house.totalCost?.toLocaleString()} WR</span>
                  </div>
                  
                  <div className="spec-item">
                    <span className="spec-label">📍 Status:</span>
                    <span className="spec-value">{house.status.replace('_', ' ').toUpperCase()}</span>
                  </div>
                  
                  {house.status === 'for_sale' && house.salePrice && (
                    <div className="spec-item price-item">
                      <span className="spec-label">💰 Sale Price:</span>
                      <span className="spec-value sale-price">{house.salePrice.toLocaleString()} WR</span>
                    </div>
                  )}
                  
                  {house.status === 'for_rent' && house.rentPrice && (
                    <div className="spec-item price-item">
                      <span className="spec-label">🏨 Rent Price:</span>
                      <span className="spec-value rent-price">{house.rentPrice} WR/night</span>
                    </div>
                  )}
                </div>
                
                <div className="house-protection-info">
                  <h5>🛡️ Safe House Features:</h5>
                  <ul>
                    <li>🖥️ Computer protection within 500m radius</li>
                    <li>🚓 SWAT hiding spot</li>
                    <li>🏠 Secure base of operations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="modal-actions">
            <button className="close-modal-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Real Estate Section Component
  const RealEstateSection = ({ user, updateUser, addNotification }) => {
    const [realEstateTab, setRealEstateTab] = useState('build');
    const [rentalTab, setRentalTab] = useState('browse');
    const [userHouse, setUserHouse] = useState(null);
    const [housesForSale, setHousesForSale] = useState([]);
    const [housesForRent, setHousesForRent] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // House viewing modal states
    const [selectedHouseForView, setSelectedHouseForView] = useState(null);
    const [showHouseViewModal, setShowHouseViewModal] = useState(false);
    
    // House builder states
    const [bedrooms, setBedrooms] = useState(1);
    const [hasLounge, setHasLounge] = useState(false);
    const [hasKitchen, setHasKitchen] = useState(false);
    const [hasDiningRoom, setHasDiningRoom] = useState(false);
    const [hasGarden, setHasGarden] = useState(false);
    
    // Marketplace states
    const [searchQuery, setSearchQuery] = useState('');
    const [bedroomFilter, setBedroomFilter] = useState('');
    const [salePrice, setSalePrice] = useState('');
    const [rentPrice, setRentPrice] = useState('');
    const [housePrices, setHousePrices] = useState({}); // Per-house price tracking
    
    // Load user's house and marketplace data
    useEffect(() => {
      if (realEstateTab === 'build' || realEstateTab === 'manage') {
        fetchUserHouse();
      } else if (realEstateTab === 'marketplace') {
        fetchHousesForSale();
      } else if (realEstateTab === 'rental') {
        fetchHousesForRent();
        // Also fetch user houses when viewing rental tab to ensure fresh data
        fetchUserHouse();
      }
    }, [realEstateTab]);
    
    // Auto-refresh house data every 30 seconds to catch rental updates
    useEffect(() => {
      const interval = setInterval(() => {
        if (realEstateTab === 'manage') {
          fetchUserHouse(); // Refresh My Houses to show new rental activity
        }
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }, [realEstateTab]);
    
    // Refresh data when switching to rental management tab
    useEffect(() => {
      if (realEstateTab === 'rental' && rentalTab === 'my-rentals') {
        fetchUserHouse(); // Refresh house data to show latest rental info
      }
    }, [realEstateTab, rentalTab]);
    
    const calculateHousePrice = () => {
      let basePrice = bedrooms * 100000;
      if (hasLounge) basePrice += 50000;
      if (hasKitchen) basePrice += 75000;
      if (hasDiningRoom) basePrice += 40000;
      if (hasGarden) basePrice += 60000;
      return basePrice;
    };
    
    const fetchUserHouse = async () => {
      try {
        const response = await api.get('/house/my-houses');
        const data = response.data;
        if (data.success) {
          setUserHouse(data.houses); // Now an array of houses
        }
      } catch (err) {
        console.error('Error fetching user houses:', err);
      }
    };
    
    const fetchHousesForSale = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (bedroomFilter) params.append('bedrooms', bedroomFilter);
        
        const response = await api.get(`/house/for-sale?${params}`);
        const data = response.data;
        if (data.success) {
          setHousesForSale(data.houses);
        }
      } catch (err) {
        console.error('Error fetching houses for sale:', err);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchHousesForRent = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (bedroomFilter) params.append('bedrooms', bedroomFilter);
        
        const response = await api.get(`/house/for-rent?${params}`);
        const data = response.data;
        if (data.success) {
          setHousesForRent(data.houses);
        }
      } catch (err) {
        console.error('Error fetching houses for rent:', err);
      } finally {
        setLoading(false);
      }
    };
    
    const handleBuildHouse = async () => {
      try {
        setLoading(true);
        
        const response = await api.post('/house/build', {
          bedrooms,
          hasLounge,
          hasKitchen,
          hasDiningRoom,
          hasGarden
        });
        
        const data = response.data;
        
        if (data.success) {
          addNotification({
            title: '🏠 House Built Successfully!',
            message: data.message,
            type: 'success'
          });
          
          // Update user balance
          updateUser({ wrCoins: data.remainingBalance });
          
          // Refresh user house data
          await fetchUserHouse();
          
          // Switch to manage tab to show the new house
          setRealEstateTab('manage');
        } else {
          addNotification({
            title: '❌ House Building Failed',
            message: data.message,
            type: 'error'
          });
        }
      } catch (err) {
        console.error('Error building house:', err);
        addNotification({
          title: '❌ House Building Error',
          message: 'Failed to build house. Please try again.',
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    const handleSellHouse = async (houseId) => {
      try {
        const houseSalePrice = housePrices[`${houseId}_sale`];
        if (!houseSalePrice || parseFloat(houseSalePrice) <= 0) {
          addNotification({
            title: '❌ Invalid Price',
            message: 'Please enter a valid sale price',
            type: 'error'
          });
          return;
        }
        
        const response = await api.post('/house/sell', {
          houseId, 
          salePrice: parseFloat(houseSalePrice)
        });
        
        const data = response.data;
        if (data.success) {
          addNotification({
            title: '🏠 House Listed for Sale!',
            message: data.message,
            type: 'success'
          });
          fetchUserHouse();
          // Clear the price for this specific house
          setHousePrices(prev => ({
            ...prev,
            [`${houseId}_sale`]: ''
          }));
        } else {
          addNotification({
            title: '❌ Listing Failed',
            message: data.message,
            type: 'error'
          });
        }
      } catch (err) {
        addNotification({
          title: '❌ Listing Error',
          message: 'Failed to list house for sale',
          type: 'error'
        });
      }
    };
    
    const handleRentOutHouse = async (houseId) => {
      try {
        const houseRentPrice = housePrices[`${houseId}_rent`];
        if (!houseRentPrice || parseFloat(houseRentPrice) <= 0) {
          addNotification({
            title: '❌ Invalid Price',
            message: 'Please enter a valid rent price per night',
            type: 'error'
          });
          return;
        }
        
        const response = await api.post('/house/rent-out', {
          houseId, 
          rentPrice: parseFloat(houseRentPrice)
        });
        
        const data = response.data;
        if (data.success) {
          addNotification({
            title: '🏠 House Listed for Rent!',
            message: data.message,
            type: 'success'
          });
          fetchUserHouse();
          // Clear the price for this specific house
          setHousePrices(prev => ({
            ...prev,
            [`${houseId}_rent`]: ''
          }));
        } else {
          addNotification({
            title: '❌ Listing Failed',
            message: data.message,
            type: 'error'
          });
        }
      } catch (err) {
        addNotification({
          title: '❌ Listing Error',
          message: 'Failed to list house for rent',
          type: 'error'
        });
      }
    };
    
    const handleBuyHouse = async (houseId) => {
      try {
        const response = await api.post('/house/buy', { houseId });
        
        const data = response.data;
        if (data.success) {
          addNotification({
            title: '🏠 House Purchased!',
            message: data.message,
            type: 'success'
          });
          updateUser({ wrCoins: data.remainingBalance });
          fetchHousesForSale();
          setRealEstateTab('manage');
        } else {
          addNotification({
            title: '❌ Purchase Failed',
            message: data.message,
            type: 'error'
          });
        }
      } catch (err) {
        addNotification({
          title: '❌ Purchase Error',
          message: 'Failed to purchase house',
          type: 'error'
        });
      }
    };

    const handleRentHouse = async (houseId) => {
      try {
        // Fixed 12-hour rental period
        const confirmed = window.confirm(`Rent this house for 12 hours (1 night stay)?\n\nYou can enter/exit multiple times during this period.`);
        if (!confirmed) return; // User cancelled

        const response = await api.post('/house/rent', { houseId });
        
        const data = response.data;
        if (data.success) {
          addNotification({
            title: '🏠 House Rented!',
            message: `Successfully rented house for 12 hours! You can enter/exit multiple times during this period.`,
            type: 'success'
          });
          updateUser({ wrCoins: data.remainingBalance });
          fetchHousesForRent();
        } else {
          addNotification({
            title: '❌ Rental Failed',
            message: data.message,
            type: 'error'
          });
        }
      } catch (err) {
        addNotification({
          title: '❌ Rental Error',
          message: 'Failed to rent house',
          type: 'error'
        });
      }
    };

    const generateEnhancedHouseImage = async (houseId) => {
      try {
        const response = await api.post(`/house/${houseId}/generate-enhanced-image`);
        
        const data = response.data;
        
        if (data.success) {
          addNotification({
            title: '🖼️ Enhanced Image Generated!',
            message: data.message,
            type: 'success'
          });
          fetchUserHouse(); // Refresh the house list to show new image
        } else {
          // Check if it's a cooldown error (status 429)
          if (response.status === 429) {
            addNotification({
              title: '⏰ Weekly Cooldown Active',
              message: data.message,
              type: 'warning'
            });
          } else {
            addNotification({
              title: '❌ Generation Failed',
              message: data.message,
              type: 'error'
            });
          }
        }
      } catch (err) {
        addNotification({
          title: '❌ Generation Error',
          message: 'Failed to generate enhanced house image',
          type: 'error'
        });
      }
    };
    
    const handleDeleteHouse = async (houseId, originalCost) => {
      // Confirm deletion with user
      const confirmDelete = window.confirm(
        `Are you sure you want to delete this house?\n\n` +
        `Original cost: ${originalCost?.toLocaleString()} WR\n` +
        `Refund: ${Math.floor(originalCost * 0.8).toLocaleString()} WR (80%)\n` +
        `Loss: ${Math.floor(originalCost * 0.2).toLocaleString()} WR (20%)\n\n` +
        `This action cannot be undone.`
      );
      
      if (!confirmDelete) return;
      
      try {
        const response = await api.delete(`/house/${houseId}/delete`);
        
        const data = response.data;
        if (data.success) {
          addNotification({
            title: '🏠 House Deleted!',
            message: data.message,
            type: 'success'
          });
          
          // Update user balance
          updateUser({ wrCoins: data.newBalance });
          
          // Refresh the house list
          fetchUserHouse();
        } else {
          addNotification({
            title: '❌ Deletion Failed',
            message: data.message,
            type: 'error'
          });
        }
      } catch (err) {
        addNotification({
          title: '❌ Deletion Error',
          message: 'Failed to delete house',
          type: 'error'
        });
      }
    };
    
    const handleStopListing = async (houseId, listingType) => {
      try {
        const response = await api.post(`/house/${houseId}/stop-listing`);
        
        const data = response.data;
        if (data.success) {
          addNotification({
            title: listingType === 'sale' ? '🚫 Taken Off Market!' : '🛑 Rental Stopped!',
            message: data.message,
            type: 'success'
          });
          
          // Refresh the house list
          fetchUserHouse();
        } else {
          addNotification({
            title: '❌ Stop Listing Failed',
            message: data.message,
            type: 'error'
          });
        }
      } catch (err) {
        addNotification({
          title: '❌ Stop Listing Error',
          message: 'Failed to stop house listing',
          type: 'error'
        });
      }
    };
    
    const handleViewHouse = async (houseId) => {
      try {
        const response = await api.get(`/house/${houseId}`);
        const data = response.data;
        
        if (data.success) {
          setSelectedHouseForView(data.house);
          setShowHouseViewModal(true);
        } else {
          addNotification({
            title: '❌ View Failed',
            message: data.message,
            type: 'error'
          });
        }
      } catch (err) {
        addNotification({
          title: '❌ View Error',
          message: 'Failed to load house details',
          type: 'error'
        });
      }
    };
    
    const closeHouseViewModal = () => {
      setShowHouseViewModal(false);
      setSelectedHouseForView(null);
    };
    
    // Owner Rentals Management Component
    const OwnerRentalsManagement = ({ user }) => {
      const [ownerRentals, setOwnerRentals] = useState(null);
      const [loading, setLoading] = useState(false);
      
      useEffect(() => {
        fetchOwnerRentals();
      }, []);
      
      // Auto-refresh rental data every 30 seconds to catch new rentals
      useEffect(() => {
        const interval = setInterval(() => {
          fetchOwnerRentals();
        }, 30000); // 30 seconds
        
        return () => clearInterval(interval);
      }, []);
      
      const fetchOwnerRentals = async () => {
        try {
          setLoading(true);
          console.log('🔄 Fetching owner rentals data...');
          const response = await api.get('/house/owner-rentals');
          
          const data = response.data;
          console.log('📊 Owner rentals response:', data);
          
          if (data.success) {
            setOwnerRentals(data);
            console.log('✅ Owner rentals loaded:', {
              activeRentals: data.activeRentals?.length || 0,
              completedRentals: data.completedRentals?.length || 0,
              totalEarnings: data.totalEarnings || 0
            });
          } else {
            console.error('❌ Owner rentals failed:', data.message);
            addNotification({
              title: '❌ Load Failed',
              message: data.message,
              type: 'error'
            });
          }
        } catch (err) {
          console.error('❌ Owner rentals error:', err);
          addNotification({
            title: '❌ Load Error',
            message: `Failed to load rental data: ${err.response?.data?.message || err.message}`,
            type: 'error'
          });
        } finally {
          setLoading(false);
        }
      };
      
      // Expose fetchOwnerRentals to global scope for refresh button
      React.useEffect(() => {
        window.refreshOwnerRentals = fetchOwnerRentals;
        return () => {
          delete window.refreshOwnerRentals;
        };
      }, []);
      
      if (loading) {
        return <div className="loading">Loading rental data...</div>;
      }
      
      if (!ownerRentals) {
        return <div className="error-message">Failed to load rental data</div>;
      }
      
      return (
        <div className="owner-rentals-management">
          {/* Rental Summary */}
          <div className="rental-summary">
            <div className="summary-stats">
              <div className="stat-card">
                <h6>💰 Total Earnings</h6>
                <span className="stat-value">{ownerRentals.totalEarnings?.toLocaleString() || 0} WR</span>
              </div>
              <div className="stat-card">
                <h6>🏠 Active Rentals</h6>
                <span className="stat-value">{ownerRentals.activeRentals?.length || 0}</span>
              </div>
              <div className="stat-card">
                <h6>📋 Completed Rentals</h6>
                <span className="stat-value">{ownerRentals.completedRentals?.length || 0}</span>
              </div>
            </div>
          </div>
          
          {/* Active Rentals */}
          {ownerRentals.activeRentals && ownerRentals.activeRentals.length > 0 && (
            <div className="active-rentals-section">
              <h6>🔴 Active Rentals</h6>
              <div className="rentals-grid">
                {ownerRentals.activeRentals.map(rental => (
                  <div key={rental.rentalId} className="rental-card active">
                    <div className="rental-header">
                      <h7>🏠 {rental.houseName}</h7>
                      <span className="tenant">👤 {rental.tenantUsername}</span>
                    </div>
                    <div className="rental-info">
                      <p><strong>💰 Rate:</strong> {rental.pricePerNight} WR/night</p>
                      <p><strong>🗓️ Nights:</strong> {rental.totalNights}</p>
                      <p><strong>💳 Total Cost:</strong> {rental.totalCost?.toLocaleString()} WR</p>
                      <p><strong>📈 Your Payment:</strong> {rental.ownerPayment?.toLocaleString()} WR</p>
                      <p><strong>⏰ Time Remaining:</strong> {Math.round(rental.hoursRemaining)} hours</p>
                      <p><strong>📅 Ends:</strong> {new Date(rental.endDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Completed Rentals */}
          {ownerRentals.completedRentals && ownerRentals.completedRentals.length > 0 && (
            <div className="completed-rentals-section">
              <h6>✅ Recent Completed Rentals</h6>
              <div className="rentals-grid">
                {ownerRentals.completedRentals.slice(0, 10).map(rental => (
                  <div key={rental.rentalId} className="rental-card completed">
                    <div className="rental-header">
                      <h7>🏠 {rental.houseName}</h7>
                      <span className="tenant">👤 {rental.tenantUsername}</span>
                    </div>
                    <div className="rental-info">
                      <p><strong>💰 Rate:</strong> {rental.pricePerNight} WR/night</p>
                      <p><strong>🗓️ Nights:</strong> {rental.totalNights}</p>
                      <p><strong>💳 Total Cost:</strong> {rental.totalCost?.toLocaleString()} WR</p>
                      <p><strong>📈 Your Payment:</strong> {rental.ownerPayment?.toLocaleString()} WR</p>
                      <p><strong>📅 Period:</strong> {new Date(rental.startDate).toLocaleDateString()} - {new Date(rental.endDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* No Rentals Message */}
          {(!ownerRentals.activeRentals || ownerRentals.activeRentals.length === 0) && 
           (!ownerRentals.completedRentals || ownerRentals.completedRentals.length === 0) && (
            <div className="no-rentals">
              <h6>🏠 No Rental History</h6>
              <p>You haven't received any rentals yet. Make sure your houses are listed for rent in "My Houses" to start earning rental income!</p>
            </div>
          )}
        </div>
      );
    };
    
    return (
      <div className="real-estate-section">
        <div className="section-header">
          <h3>🏠 Real Estate Manager</h3>
          <p>Build your perfect safe house with custom rooms. Houses protect computers and provide SWAT hiding spots within 500m radius.</p>
        </div>
        
        {/* Real Estate Navigation */}
        <div className="real-estate-nav">
          <button 
            className={`real-estate-nav-btn ${realEstateTab === 'build' ? 'active' : ''}`}
            onClick={() => setRealEstateTab('build')}
          >
            🔨 Build House
          </button>
          <button 
            className={`real-estate-nav-btn ${realEstateTab === 'manage' ? 'active' : ''}`}
            onClick={() => setRealEstateTab('manage')}
          >
            🏠 My Houses
          </button>
          <button 
            className={`real-estate-nav-btn ${realEstateTab === 'marketplace' ? 'active' : ''}`}
            onClick={() => setRealEstateTab('marketplace')}
          >
            🛒 House Market
          </button>
          <button 
            className={`real-estate-nav-btn ${realEstateTab === 'rental' ? 'active' : ''}`}
            onClick={() => setRealEstateTab('rental')}
          >
            🏨 House Rentals
          </button>
        </div>
        
        {/* Build House Tab */}
        {realEstateTab === 'build' && (
          <div className="house-builder">
            <h4>🔨 Custom House Builder</h4>
            <p className="builder-description">Build unlimited houses! Each house goes to the occult bank to fund mysterious operations.</p>
            <div className="house-builder-form">
                  <div className="house-specs">
                    <div className="spec-group">
                      <label>Bedrooms (1-6): {bedrooms * 100000} WR</label>
                      <input
                        type="range"
                        min="1"
                        max="6"
                        value={bedrooms}
                        onChange={(e) => setBedrooms(parseInt(e.target.value))}
                        className="bedroom-slider"
                      />
                      <span>{bedrooms} bedroom{bedrooms > 1 ? 's' : ''}</span>
                    </div>
                    
                    <div className="optional-rooms">
                      <div className="room-option">
                        <label>
                          <input
                            type="checkbox"
                            checked={hasLounge}
                            onChange={(e) => setHasLounge(e.target.checked)}
                          />
                          Lounge (+50,000 WR)
                        </label>
                      </div>
                      <div className="room-option">
                        <label>
                          <input
                            type="checkbox"
                            checked={hasKitchen}
                            onChange={(e) => setHasKitchen(e.target.checked)}
                          />
                          Kitchen (+75,000 WR)
                        </label>
                      </div>
                      <div className="room-option">
                        <label>
                          <input
                            type="checkbox"
                            checked={hasDiningRoom}
                            onChange={(e) => setHasDiningRoom(e.target.checked)}
                          />
                          Dining Room (+40,000 WR)
                        </label>
                      </div>
                      <div className="room-option">
                        <label>
                          <input
                            type="checkbox"
                            checked={hasGarden}
                            onChange={(e) => setHasGarden(e.target.checked)}
                          />
                          Garden (+60,000 WR)
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="house-summary">
                    <h5>House Summary</h5>
                    <p><strong>Bedrooms:</strong> {bedrooms}</p>
                    {hasLounge && <p><strong>Lounge:</strong> Yes</p>}
                    {hasKitchen && <p><strong>Kitchen:</strong> Yes</p>}
                    {hasDiningRoom && <p><strong>Dining Room:</strong> Yes</p>}
                    {hasGarden && <p><strong>Garden:</strong> Yes</p>}
                    <div className="total-cost">
                      <strong>Total Cost: {calculateHousePrice().toLocaleString()} WR</strong>
                    </div>
                    <div className="your-balance">
                      Your Balance: {(user?.wrCoins || 0).toFixed(2)} WR
                    </div>
                  </div>
                  
                  <button
                    className="build-house-btn"
                    onClick={handleBuildHouse}
                    disabled={loading || (user?.wrCoins || 0) < calculateHousePrice()}
                  >
                    {loading ? 'Building...' : `Build House (${calculateHousePrice().toLocaleString()} WR)`}
                  </button>
                </div>
          </div>
        )}
        
        {/* Manage Houses Tab */}
        {realEstateTab === 'manage' && (
          <div className="house-management">
            {userHouse && userHouse.length > 0 ? (
              <div className="user-houses-grid">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4>🏠 Your Real Estate Portfolio ({userHouse.length} Houses)</h4>
                  <button 
                    onClick={fetchUserHouse}
                    style={{
                      background: '#00ff41',
                      color: '#000',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    🔄 Refresh Houses
                  </button>
                </div>
                <div className="houses-grid">
                  {userHouse.map((house, index) => (
                    <div key={house._id || index} className="house-card">
                      <div className="house-header">
                        <h5>🏠 House #{index + 1}</h5>
                        <span className={`house-status ${house.status}`}>
                          {house.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      
                      {house.houseImageUrl ? (
                        <img 
                          src={house.houseImageUrl} 
                          alt={`House ${index + 1}`} 
                          className="house-image"
                          onError={(e) => {
                            console.log('House image failed to load:', house.houseImageUrl);
                            // Prevent infinite loop by checking if we're already showing fallback
                            if (!e.target.src.includes('data:image/svg')) {
                              const svgContent = `<svg width="200" height="150" xmlns="http://www.w3.org/2000/svg">
                                <rect width="200" height="150" fill="#4a90e2"/>
                                <text x="100" y="75" text-anchor="middle" dy=".3em" fill="white" font-family="Arial" font-size="14">
                                  House ${house.bedrooms}BR
                                </text>
                              </svg>`;
                              e.target.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
                            }
                          }}
                          onLoad={() => {
                            console.log('House image loaded successfully:', house.houseImageUrl);
                          }}
                        />
                      ) : (
                        <div 
                          className="house-image-placeholder"
                          style={{
                            width: '100%',
                            height: '150px',
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#666',
                            fontSize: '14px',
                            border: '2px dashed #ccc'
                          }}
                        >
                          🏠 No Image Available
                        </div>
                      )}
                      
                      {/* Enhanced Image Generation Button */}
                      <div className="image-controls" style={{ margin: '10px 0', textAlign: 'center' }}>
                        <button
                          onClick={() => generateEnhancedHouseImage(house._id)}
                          style={{
                            background: '#ff8800',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginRight: '10px'
                          }}
                        >
                          🎨 Generate New Picture
                        </button>
                        <button
                          onClick={() => handleDeleteHouse(house._id, house.totalCost)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          🗑️ Delete House
                        </button>
                        <p style={{ fontSize: '10px', color: '#888', margin: '5px 0 0 0' }}>
                          Enhanced image includes house data & price | Delete gives 80% refund
                        </p>
                      </div>
                      
                      <div className="house-info">
                        <p><strong>Bedrooms:</strong> {house.bedrooms}</p>
                        {house.hasLounge && <p>✅ Lounge</p>}
                        {house.hasKitchen && <p>✅ Kitchen</p>}
                        {house.hasDiningRoom && <p>✅ Dining Room</p>}
                        {house.hasGarden && <p>✅ Garden</p>}
                        <p><strong>Build Cost:</strong> {house.totalCost?.toLocaleString()} WR</p>
                        {house.location?.lat !== 0 && house.location?.lng !== 0 ? (
                          <p><strong>Location:</strong> Placed on map</p>
                        ) : (
                          <p><strong>Location:</strong> Not placed yet</p>
                        )}
                      </div>
                      
                      {house.status === 'owned' && (
                        <div className="house-actions">
                          {house.location?.lat === 0 && house.location?.lng === 0 ? (
                            <div className="place-house-notice">
                              <p>📍 Place this house on the map to sell or rent it.</p>
                              <button 
                                className="place-house-btn"
                                onClick={() => navigate('/hack-the-planet')}
                              >
                                🗺️ Place House on Map
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="action-group">
                                <h6>Sell House</h6>
                                <div className="sell-form">
                                  <input
                                    type="number"
                                    placeholder="Sale price"
                                    value={housePrices[`${house._id}_sale`] || ''}
                                    onChange={(e) => setHousePrices(prev => ({
                                      ...prev,
                                      [`${house._id}_sale`]: e.target.value
                                    }))}
                                    className="price-input"
                                  />
                                  <button 
                                    onClick={() => handleSellHouse(house._id)} 
                                    disabled={!housePrices[`${house._id}_sale`]}
                                  >
                                    List for Sale
                                  </button>
                                </div>
                              </div>
                              
                              <div className="action-group">
                                <h6>Rent Out House</h6>
                                <div className="rent-form">
                                  <input
                                    type="number"
                                    placeholder="Rent per night"
                                    value={housePrices[`${house._id}_rent`] || ''}
                                    onChange={(e) => setHousePrices(prev => ({
                                      ...prev,
                                      [`${house._id}_rent`]: e.target.value
                                    }))}
                                    className="price-input"
                                  />
                                  <button 
                                    onClick={() => handleRentOutHouse(house._id)} 
                                    disabled={!housePrices[`${house._id}_rent`]}
                                  >
                                    List for Rent
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* House is for sale */}
                      {house.status === 'for_sale' && (
                        <div className="house-actions">
                          <div className="listing-status">
                            <h6>🏷️ Listed for Sale</h6>
                            <p><strong>Sale Price:</strong> {house.salePrice?.toLocaleString()} WR</p>
                            <button 
                              onClick={() => handleStopListing(house._id, 'sale')}
                              style={{
                                background: '#ffc107',
                                color: '#000',
                                border: 'none',
                                padding: '8px 12px',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                margin: '10px 0'
                              }}
                            >
                              🚫 Take Off Market
                            </button>
                            <p style={{ fontSize: '10px', color: '#888' }}>
                              Remove house from sale listings
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* House is for rent */}
                      {house.status === 'for_rent' && (
                        <div className="house-actions">
                          <div className="listing-status">
                            <h6>🏠 Listed for Rent</h6>
                            <p><strong>Rent Price:</strong> {house.rentPrice?.toLocaleString()} WR/night</p>
                            
                            {/* Show active rentals if any */}
                            {house.hasActiveRentals && house.currentTenants && house.currentTenants.length > 0 && (
                              <div style={{ 
                                background: '#e8f5e8', 
                                padding: '10px', 
                                borderRadius: '5px', 
                                margin: '10px 0',
                                border: '1px solid #4caf50'
                              }}>
                                <h6 style={{ color: '#2e7d32', margin: '0 0 8px 0' }}>
                                  🏘️ Active Rentals ({house.rentalCount})
                                </h6>
                                {house.currentTenants.map((tenant, index) => (
                                  <div key={index} style={{ 
                                    marginBottom: '5px', 
                                    fontSize: '12px',
                                    padding: '5px',
                                    background: 'white',
                                    borderRadius: '3px'
                                  }}>
                                    <strong>👤 {tenant.username}</strong><br/>
                                    📅 Until: {new Date(tenant.endDate).toLocaleDateString()}<br/>
                                    💰 Paying: {tenant.pricePerNight} WR/night
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            <button 
                              onClick={() => handleStopListing(house._id, 'rent')}
                              style={{
                                background: '#fd7e14',
                                color: 'white',
                                border: 'none',
                                padding: '8px 12px',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                margin: '10px 0'
                              }}
                            >
                              🛑 Stop Rental Listing
                            </button>
                            <p style={{ fontSize: '10px', color: '#888' }}>
                              Remove house from rental listings {house.hasActiveRentals ? '(current rentals continue)' : ''}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* House is rented */}
                      {house.status === 'rented' && (
                        <div className="house-actions">
                          <div className="listing-status">
                            <h6>🏘️ Currently Rented</h6>
                            <p><strong>Tenant:</strong> {house.currentTenantUsername}</p>
                            <p><strong>Rent:</strong> {house.rentPrice?.toLocaleString()} WR/night</p>
                            {house.rentEndDate && (
                              <p><strong>Rental Ends:</strong> {new Date(house.rentEndDate).toLocaleDateString()}</p>
                            )}
                            <p style={{ fontSize: '10px', color: '#888', fontStyle: 'italic' }}>
                              Cannot modify while rented
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-house">
                <h4>🏗️ No Houses Found</h4>
                <p>You don't own any houses yet. Start building your real estate empire!</p>
                <button onClick={() => setRealEstateTab('build')} className="build-first-house-btn">
                  Build Your First House
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* House Marketplace Tab */}
        {realEstateTab === 'marketplace' && (
          <div className="house-marketplace">
            <h4>🛒 Houses for Sale</h4>
            
            <div className="marketplace-filters">
              <input
                type="text"
                placeholder="Search by owner username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <select
                value={bedroomFilter}
                onChange={(e) => setBedroomFilter(e.target.value)}
                className="bedroom-filter"
              >
                <option value="">All Bedrooms</option>
                <option value="1">1 Bedroom</option>
                <option value="2">2 Bedrooms</option>
                <option value="3">3 Bedrooms</option>
                <option value="4">4 Bedrooms</option>
                <option value="5">5 Bedrooms</option>
                <option value="6">6 Bedrooms</option>
              </select>
              <button onClick={fetchHousesForSale} className="search-btn">Search</button>
            </div>
            
            {loading ? (
              <div className="loading">Loading houses...</div>
            ) : housesForSale.length === 0 ? (
              <div className="no-houses">No houses for sale found.</div>
            ) : (
              <div className="houses-grid">
                {housesForSale.map(house => (
                  <div key={house._id} className="house-card marketplace-card">
                    <div className="house-header">
                      <h5>🏠 {house.bedrooms} Bedroom House</h5>
                      <span className="owner">by {house.ownerUsername}</span>
                    </div>
                    
                    {house.houseImageUrl && (
                      <img src={house.houseImageUrl} alt="House" className="house-image" />
                    )}
                    
                    <div className="house-features">
                      {house.hasLounge && <span className="feature">🛋️ Lounge</span>}
                      {house.hasKitchen && <span className="feature">🍳 Kitchen</span>}
                      {house.hasDiningRoom && <span className="feature">🍽️ Dining</span>}
                      {house.hasGarden && <span className="feature">🌺 Garden</span>}
                    </div>
                    
                    <div className="house-price">
                      <strong>Price: {house.salePrice?.toLocaleString()} WR</strong>
                    </div>
                    
                    <div className="house-card-actions">
                      <button
                        className="view-house-btn"
                        onClick={() => handleViewHouse(house._id)}
                      >
                        👁️ View Details
                      </button>
                      <button
                        className="buy-house-btn"
                        onClick={() => handleBuyHouse(house._id)}
                        disabled={(user?.wrCoins || 0) < house.salePrice}
                      >
                        Buy House
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* House Rentals Tab */}
        {realEstateTab === 'rental' && (
          <div className="house-rentals">
            <h4>🏨 House Rental System</h4>
            
            {/* Rental Sub-navigation */}
            <div className="rental-nav">
              <button 
                className={`rental-nav-btn ${rentalTab === 'browse' ? 'active' : ''}`}
                onClick={() => setRentalTab('browse')}
              >
                🔍 Browse Rentals
              </button>
              <button 
                className={`rental-nav-btn ${rentalTab === 'my-rentals' ? 'active' : ''}`}
                onClick={() => setRentalTab('my-rentals')}
              >
                🏠 My Rental Properties
              </button>
            </div>
            
            {/* Browse Houses for Rent */}
            {rentalTab === 'browse' && (
              <div className="browse-rentals">
                <h5>🔍 Browse Houses for Rent</h5>
                
                <div className="marketplace-filters">
                  <input
                    type="text"
                    placeholder="Search by owner username..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <select
                    value={bedroomFilter}
                    onChange={(e) => setBedroomFilter(e.target.value)}
                    className="bedroom-filter"
                  >
                    <option value="">All Bedrooms</option>
                    <option value="1">1 Bedroom</option>
                    <option value="2">2 Bedrooms</option>
                    <option value="3">3 Bedrooms</option>
                    <option value="4">4 Bedrooms</option>
                    <option value="5">5 Bedrooms</option>
                    <option value="6">6 Bedrooms</option>
                  </select>
                  <button onClick={fetchHousesForRent} className="search-btn">Search</button>
                </div>
                
                {loading ? (
                  <div className="loading">Loading houses...</div>
                ) : housesForRent.length === 0 ? (
                  <div className="no-houses">No houses for rent found.</div>
                ) : (
                  <div className="houses-grid">
                    {housesForRent.map(house => (
                      <div key={house._id} className="house-card marketplace-card">
                        <div className="house-header">
                          <h5>🏠 {house.bedrooms} Bedroom House</h5>
                          <span className="owner">by {house.ownerUsername}</span>
                        </div>
                        
                        {house.houseImageUrl && (
                          <img src={house.houseImageUrl} alt="House" className="house-image" />
                        )}
                        
                        <div className="house-features">
                          {house.hasLounge && <span className="feature">🛋️ Lounge</span>}
                          {house.hasKitchen && <span className="feature">🍳 Kitchen</span>}
                          {house.hasDiningRoom && <span className="feature">🍽️ Dining</span>}
                          {house.hasGarden && <span className="feature">🌺 Garden</span>}
                        </div>
                        
                        <div className="house-price">
                          <strong>Rent: {house.rentPrice} WR/night</strong>
                        </div>
                        
                        <div className="house-card-actions">
                          <button
                            className="view-house-btn"
                            onClick={() => handleViewHouse(house._id)}
                          >
                            👁️ View Details
                          </button>
                          <button 
                            className="rent-house-btn"
                            onClick={() => handleRentHouse(house._id)}
                          >
                            🏠 Rent House ({house.rentPrice} WR/night)
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* My Rental Properties */}
            {rentalTab === 'my-rentals' && (
              <div className="my-rental-properties">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <div>
                    <h5>🏠 My Rental Properties</h5>
                    <p>Manage rentals for houses you own and view your rental income.</p>
                  </div>
                  <button 
                    onClick={() => {
                      // Force refresh both My Houses and Owner Rentals
                      fetchUserHouse();
                      // Also trigger refresh of owner rentals data
                      if (window.refreshOwnerRentals) {
                        window.refreshOwnerRentals();
                      }
                    }}
                    style={{
                      background: '#00ff41',
                      color: '#000',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    🔄 Refresh Data
                  </button>
                </div>
                
                <OwnerRentalsManagement user={user} key={`rental-${Date.now()}`} />
              </div>
            )}
          </div>
        )}
        
        {/* House View Modal */}
        <HouseViewModal 
          house={selectedHouseForView}
          isOpen={showHouseViewModal}
          onClose={closeHouseViewModal}
        />
      </div>
    );
  };

  // Career functions
  const fetchCareerData = async () => {
    try {
      setCareerLoading(true);
      setCareerError(null);
      
      const response = await api.get('/career/overview');
      
      const data = response.data;
      
      if (data.success) {
        setCareerData(data.data);
      } else {
        setCareerError(data.message);
      }
    } catch (error) {
      console.error('Error fetching career data:', error);
      setCareerError('Failed to load career information');
    } finally {
      setCareerLoading(false);
    }
  };

  const fetchStreakData = async () => {
    try {
      setStreakLoading(true);
      
      const response = await api.get('/career/streak-status');
      
      const data = response.data;
      
      if (data.success) {
        setStreakData(data.data);
      }
    } catch (error) {
      console.error('Error fetching streak data:', error);
    } finally {
      setStreakLoading(false);
    }
  };

  const handleJobApplication = async (officeId, jobLevel) => {
    try {
      setCareerLoading(true);
      
      const response = await api.post('/career/apply', {
        officeId,
        jobLevel
      });
      
      const data = response.data;
      
      if (data.success) {
        addNotification({
          title: '🎉 Application Successful!',
          message: data.message,
          type: 'success'
        });
        await fetchCareerData();
        await fetchStreakData();
      } else {
        addNotification({
          title: '❌ Application Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error applying for job:', error);
      addNotification({
        title: '❌ Application Error',
        message: 'Failed to submit job application',
        type: 'error'
      });
    } finally {
      setCareerLoading(false);
    }
  };

  const handleStartWork = async () => {
    try {
      setCareerLoading(true);
      
      const response = await api.post('/career/start-work');
      
      const data = response.data;
      
      if (data.success) {
        addNotification({
          title: '💼 Work Started!',
          message: data.message,
          type: 'success'
        });
        await fetchCareerData();
        await fetchStreakData();
      } else {
        addNotification({
          title: '❌ Cannot Start Work',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error starting work:', error);
      
      // Check if it's a 400 error with a specific message from server
      const errorMessage = error.response?.data?.message || 'Failed to start work session';
      
      addNotification({
        title: '❌ Work Error',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setCareerLoading(false);
    }
  };

  // Helper function to check if applications are allowed
  const canApplyForJobs = () => {
    return careerData.applicationStatus?.canApply === true;
  };

  const getApplicationDisabledReason = () => {
    if (careerData.applicationStatus?.hasPendingApplication) {
      return `Application pending - decision in ${careerData.applicationStatus.timeRemaining}`;
    }
    if (careerData.applicationStatus?.hasRejectionCooldown) {
      return `Cooldown period - can apply again in ${careerData.applicationStatus.timeRemaining}`;
    }
    return '';
  };

  const handleQuitJob = async () => {
    // Confirm before quitting
    if (!window.confirm('Are you sure you want to quit your job? You will lose your current employment and can apply for new positions.')) {
      return;
    }

    try {
      setCareerLoading(true);
      
      const response = await api.post('/career/quit');
      
      const data = response.data;
      
      if (data.success) {
        addNotification({
          title: '📋 Job Resignation',
          message: data.message,
          type: 'success'
        });
        await fetchCareerData();
        await fetchStreakData();
      } else {
        addNotification({
          title: '❌ Cannot Quit Job',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error quitting job:', error);
      addNotification({
        title: '❌ Resignation Error',
        message: 'Failed to quit job',
        type: 'error'
      });
    } finally {
      setCareerLoading(false);
    }
  };

  return (
    <div className="shopping-container">
      <div className="content-section">
        <div className="content-section-header">
          <h2>🏪 Lifestyle</h2>
          <p>Browse our selection of computer parts, accommodation bookings, and real estate properties.</p>
        </div>
        
        {/* Main Shopping Tabs */}
        <div className="shopping-tab-navigation">
          <button 
            className={`shopping-tab-button ${activeTab === 'computer-store' ? 'active' : ''}`}
            onClick={() => setActiveTab('computer-store')}
          >
            ⚔️ Cyber Warfare Market
          </button>
          <button 
            className={`shopping-tab-button ${activeTab === 'bank' ? 'active' : ''}`}
            onClick={() => setActiveTab('bank')}
          >
            🏦 Bank
          </button>
          <button 
            className={`shopping-tab-button ${activeTab === 'accommodation' ? 'active' : ''}`}
            onClick={() => setActiveTab('accommodation')}
          >
            🏨 Accommodation
          </button>
          <button 
            className={`shopping-tab-button ${activeTab === 'real-estate' ? 'active' : ''}`}
            onClick={() => setActiveTab('real-estate')}
          >
            🏢 Real Estate
          </button>
          <button 
            className={`shopping-tab-button ${activeTab === 'businesses' ? 'active' : ''}`}
            onClick={() => setActiveTab('businesses')}
          >
            🏪 Businesses
          </button>
          <button 
            className={`shopping-tab-button ${activeTab === 'career' ? 'active' : ''}`}
            onClick={() => setActiveTab('career')}
          >
            💼 Career
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'computer-store' && (
          <div className="tab-content">
            <Parts />
          </div>
        )}

        {activeTab === 'bank' && (
          <div className="tab-content">
            <div className="bank-section">
              <div className="section-header">
                <h3>🏦 White Rabbit Bank</h3>
                <p>Complete banking services for hackers. Savings (5% PAY), loans, credit, mortgages, and transfers. All funds backed by the occult bank.</p>
              </div>

              {bankLoading && (
                <div className="loading">Loading banking data...</div>
              )}

              {bankError && (
                <div className="error-message">{bankError}</div>
              )}

              {!bankLoading && !bankError && (
                <>
                  {/* Bank Service Navigation */}
                  <div className="bank-nav">
                    <button 
                      className={`bank-nav-btn ${activeService === 'overview' ? 'active' : ''}`}
                      onClick={() => setActiveService('overview')}
                    >
                      💰 Overview
                    </button>
                    <button 
                      className={`bank-nav-btn ${activeService === 'savings' ? 'active' : ''}`}
                      onClick={() => setActiveService('savings')}
                    >
                      🏛️ Savings (5% PAY)
                    </button>
                    <button 
                      className={`bank-nav-btn ${activeService === 'credit' ? 'active' : ''}`}
                      onClick={() => setActiveService('credit')}
                    >
                      💳 Credit
                    </button>
                    <button 
                      className={`bank-nav-btn ${activeService === 'loans' ? 'active' : ''}`}
                      onClick={() => setActiveService('loans')}
                    >
                      📋 Loans
                    </button>
                    <button 
                      className={`bank-nav-btn ${activeService === 'mortgage' ? 'active' : ''}`}
                      onClick={() => setActiveService('mortgage')}
                    >
                      🏠 Mortgage
                    </button>
                    <button 
                      className={`bank-nav-btn ${activeService === 'transfer' ? 'active' : ''}`}
                      onClick={() => setActiveService('transfer')}
                    >
                      📤 Transfer
                    </button>
                  </div>

                  {/* Overview Tab */}
                  {activeService === 'overview' && (
                    <BankOverview 
                      user={user} 
                      bankingData={bankingData}
                      onRefresh={fetchBankingData}
                    />
                  )}

                  {/* Savings Tab */}
                  {activeService === 'savings' && (
                    <BankSavings 
                      user={user} 
                      bankingData={bankingData}
                      onAction={handleBankingAction}
                    />
                  )}

                  {/* Credit Tab */}
                  {activeService === 'credit' && (
                    <BankCredit 
                      user={user} 
                      bankingData={bankingData}
                      onAction={handleBankingAction}
                    />
                  )}

                  {/* Loans Tab */}
                  {activeService === 'loans' && (
                    <BankLoans 
                      user={user} 
                      bankingData={bankingData}
                      onAction={handleBankingAction}
                    />
                  )}

                  {/* Mortgage Tab */}
                  {activeService === 'mortgage' && (
                    <BankMortgage 
                      user={user} 
                      bankingData={bankingData}
                      onAction={handleBankingAction}
                    />
                  )}

                  {/* Transfer Tab */}
                  {activeService === 'transfer' && (
                    <BankTransfer 
                      user={user} 
                      onAction={handleBankingAction}
                    />
                  )}
                </>
              )}

              {/* Security Notice */}
              <div className="bank-security-notice">
                <div className="security-header">
                  <span className="security-icon">🔒</span>
                  <h4>Bank Security Notice</h4>
                </div>
                <p>
                  White Rabbit Bank uses military-grade encryption. Missing 2+ payments results in asset seizure and jail time.
                  <strong> All transactions are final.</strong>
                </p>
              </div>

            </div>
          </div>
        )}

        {activeTab === 'accommodation' && (
          <div className="tab-content">
            <div className="accommodation-section">
              <div className="section-header">
                <h3>🏨 Accommodation Bookings</h3>
                <p>Book secure hotels worldwide for your hacking missions. Safe zones with 3000m radius protection.</p>
              </div>

              {/* Accommodation Sub-tabs */}
              <div className="accommodation-tabs">
                <button 
                  className={`accommodation-tab-button ${accommodationTab === 'hotels' ? 'active' : ''}`}
                  onClick={() => setAccommodationTab('hotels')}
                >
                  🏨 Hotels
                </button>
                <button 
                  className={`accommodation-tab-button ${accommodationTab === 'bookings' ? 'active' : ''}`}
                  onClick={() => setAccommodationTab('bookings')}
                >
                  📋 My Bookings
                </button>
              </div>

              {accommodationTab === 'hotels' && (
                <div className="hotels-section">
                  <div className="hotels-controls">
                    <div className="search-controls">
                      <input
                        type="text"
                        placeholder="Search hotels or countries..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                      />
                    </div>
                    
                    <div className="filter-controls">
                      <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className="country-filter"
                      >
                        <option value="all">All Countries</option>
                        {countries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {loading && <div className="loading">Loading hotels...</div>}
                  {error && <div className="error-message">{error}</div>}

                  <div className="hotels-grid">
                    {filteredHotels.map(hotel => (
                      <div key={hotel.hotelId} className="hotel-card">
                        <div className="hotel-header">
                          <h4 className="hotel-name">{hotel.name}</h4>
                          <div className="hotel-rating">{getStarRating(hotel.rating)}</div>
                        </div>
                        
                        <div className="hotel-info">
                          <p className="hotel-country">📍 {hotel.country}</p>
                          <p className="hotel-rooms">
                            🏨 {hotel.availableRooms}/{hotel.totalRooms} rooms available
                          </p>
                          <p className="hotel-price">💰 {hotel.pricePerNight} WR/night</p>
                        </div>

                        <div className="hotel-amenities">
                          {hotel.amenities.slice(0, 3).map(amenity => (
                            <span key={amenity} className="amenity-tag">{amenity}</span>
                          ))}
                          {hotel.amenities.length > 3 && (
                            <span className="amenity-tag">+{hotel.amenities.length - 3} more</span>
                          )}
                        </div>

                        <div className="hotel-actions">
                          <button
                            className="book-button"
                            onClick={() => {
                              setSelectedHotel(hotel);
                              setShowBookingModal(true);
                            }}
                            disabled={hotel.isFullyBooked}
                          >
                            {hotel.isFullyBooked ? '🔒 Fully Booked' : '🏨 Book Now'}
                          </button>
                          
                          <button
                            className="move-button"
                            onClick={() => handleMoveToHotel(hotel)}
                          >
                            📍 Move Computer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {accommodationTab === 'bookings' && (
                <div className="bookings-section">
                  <h4>My Bookings</h4>
                  
                  {userBookings.length === 0 ? (
                    <div className="no-bookings">
                      <p>No hotel bookings found. Book your first hotel room!</p>
                    </div>
                  ) : (
                    <div className="bookings-list">
                      {userBookings.map(booking => (
                        <div key={booking.bookingId} className="booking-card">
                          <div className="booking-header">
                            <h5>{booking.hotel?.name || 'Unknown Hotel'}</h5>
                            <span className={`booking-status ${booking.status}`}>
                              {booking.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          
                          <div className="booking-info">
                            <p>📅 Check-in: {formatDate(booking.checkInDate)}</p>
                            <p>📅 Check-out: {formatDate(booking.checkOutDate)}</p>
                            <p>🏠 Room: {booking.roomNumber}</p>
                            <p>💰 Total: {booking.totalCost} WR</p>
                            <p>🌙 Nights: {booking.totalNights}</p>
                          </div>

                          <div className="booking-actions">
                            {booking.status === 'booked' && (
                              <button
                                className="checkin-button"
                                onClick={() => handleCheckIn(booking)}
                              >
                                🏨 Check In
                              </button>
                            )}
                            
                            {booking.status === 'checked_in' && (
                              <button
                                className="checkout-button"
                                onClick={() => handleCheckOut(booking)}
                              >
                                🚪 Check Out
                              </button>
                            )}
                            
                            {booking.hotel && (
                              <button
                                className="move-button"
                                onClick={() => handleMoveToHotel(booking.hotel)}
                              >
                                📍 Move to Hotel
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'real-estate' && (
          <div className="tab-content">
            <RealEstateSection user={user} updateUser={updateUser} addNotification={addNotification} />
          </div>
        )}

        {activeTab === 'businesses' && (
          <div className="tab-content">
            <div className="businesses-section">
              <div className="section-header">
                <h3>🏪 Business Empire</h3>
                <p>Build your business empire and create a survival supply chain. You can own up to 2 of each business type with different XP and coin requirements.</p>
              </div>


              {/* Business Tabs */}
              <div className="business-tab-navigation">
                <button 
                  className={`business-tab-button ${businessTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setBusinessTab('overview')}
                >
                  📋 Overview
                </button>
                <button 
                  className={`business-tab-button ${businessTab === 'create' ? 'active' : ''}`}
                  onClick={() => setBusinessTab('create')}
                  disabled={false}
                  title="Create up to 2 of each business type"
                >
                  🏗️ Create Business
                </button>
                <button 
                  className={`business-tab-button ${businessTab === 'manage' ? 'active' : ''}`}
                  onClick={() => setBusinessTab('manage')}
                  disabled={userBusiness === null}
                >
                  🔧 Manage
                </button>
              </div>

              {/* Global Business Selector - shown on all tabs when user has multiple businesses */}
              {userBusiness && userBusiness.length > 1 && (businessTab === 'manage' || businessTab === 'overview') && (
                <div className="global-business-selector">
                  <div className="selector-header">
                    <h5>🎯 Active Business</h5>
                    <p>Select which business you want to focus on for operations and viewing</p>
                  </div>
                  <div className="global-business-selector-buttons">
                    {userBusiness.map((business, index) => (
                      <button 
                        key={business._id}
                        onClick={() => setSelectedBusinessIndex(index)}
                        className={`global-business-btn ${selectedBusinessIndex === index ? 'active' : ''}`}
                        title={`${business.businessName} - ${business.businessType} business`}
                      >
                        <div className="business-btn-content">
                          <span className="business-icon">
                            {business.businessType === 'farm' && '🚜'}
                            {business.businessType === 'fishing' && '🎣'}
                            {business.businessType === 'factory' && '🏭'}
                            {business.businessType === 'warehouse' && '🏬'}
                            {business.businessType === 'shop' && '🏪'}
                          </span>
                          <div className="business-info">
                            <span className="business-name">{business.businessName}</span>
                            <span className="business-type">{business.businessType}</span>
                            <span className="business-stats">{business.supplyLevel || 0}/{business.maxCapacity || 250} supplies</span>
                          </div>
                          {selectedBusinessIndex === index && (
                            <span className="active-indicator">✅</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="current-business-summary">
                    <span className="current-label">Currently Managing:</span>
                    <span className="current-business">
                      {(() => {
                        const current = userBusiness[selectedBusinessIndex] || userBusiness[0];
                        return `${current?.businessName} (${current?.businessType})`;
                      })()}
                    </span>
                  </div>
                </div>
              )}

              {/* Business Tab Content */}
              {businessTab === 'overview' && (
                <div className="business-overview">
                  <h4>🔗 Your Supply Chain Network</h4>
                  
                  {/* User's Business Network Flow */}
                  {userBusiness && userBusiness.length > 0 ? (
                    <div className="user-supply-chain">
                      <div className="flow-header">
                        <h5>📊 Your Businesses ({userBusiness.length}/4)</h5>
                        <p style={{ fontSize: '12px', color: '#888', marginBottom: '20px' }}>
                          Visual representation of your business network and supply flow connections
                        </p>
                      </div>

                      {/* Dynamic Business Flow Visualization */}
                      <div className="business-flow-network">
                        {/* Group businesses by type for flow display */}
                        {(() => {
                          const businessesByType = {
                            farm: userBusiness.filter(b => b.businessType === 'farm'),
                            fishing: userBusiness.filter(b => b.businessType === 'fishing'),
                            factory: userBusiness.filter(b => b.businessType === 'factory'),
                            warehouse: userBusiness.filter(b => b.businessType === 'warehouse'),
                            shop: userBusiness.filter(b => b.businessType === 'shop')
                          };

                          return (
                            <div className="flow-visualization">
                              {/* Level 4: Producers (Farms/Fishing) */}
                              {(businessesByType.farm.length > 0 || businessesByType.fishing.length > 0) && (
                                <div className="flow-level producers">
                                  <div className="level-header">
                                    <span className="level-badge">Level 4 - Producers</span>
                                  </div>
                                  <div className="businesses-row">
                                    {businessesByType.farm.map((business) => (
                                      <div key={business._id} className="business-flow-card farm">
                                        <div className="business-header">
                                          <span className="business-icon">🚜</span>
                                          <h6>{business.businessName}</h6>
                                        </div>
                                        <div className="business-stats">
                                          <div className="stat">
                                            <span className="label">Supplies:</span>
                                            <span className="value">{business.supplyLevel || 0}/{business.maxCapacity || 250}</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">Factory Price:</span>
                                            <span className="value">{business.pricing?.sellToFactoryPrice || 15} WR</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">Owner Price:</span>
                                            <span className="value">{business.pricing?.sellToOwnerPrice || 25} WR</span>
                                          </div>
                                        </div>
                                        <div className="connections">
                                          <span className="connection-label">Supplies to:</span>
                                          <div className="connection-list">
                                            {business.supplyChain?.connectedCustomers?.length > 0 ? (
                                              business.supplyChain.connectedCustomers.map((customer) => (
                                                <span key={customer.businessId} className="connection">
                                                  {customer.businessType === 'factory' ? '🏭' : '📦'} {customer.businessName}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="no-connections">No connections</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    {businessesByType.fishing.map((business) => (
                                      <div key={business._id} className="business-flow-card fishing">
                                        <div className="business-header">
                                          <span className="business-icon">🎣</span>
                                          <h6>{business.businessName}</h6>
                                        </div>
                                        <div className="business-stats">
                                          <div className="stat">
                                            <span className="label">Supplies:</span>
                                            <span className="value">{business.supplyLevel || 0}/{business.maxCapacity || 250}</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">Factory Price:</span>
                                            <span className="value">{business.pricing?.sellToFactoryPrice || 15} WR</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">Owner Price:</span>
                                            <span className="value">{business.pricing?.sellToOwnerPrice || 25} WR</span>
                                          </div>
                                        </div>
                                        <div className="connections">
                                          <span className="connection-label">Supplies to:</span>
                                          <div className="connection-list">
                                            {business.supplyChain?.connectedCustomers?.length > 0 ? (
                                              business.supplyChain.connectedCustomers.map((customer) => (
                                                <span key={customer.businessId} className="connection">
                                                  {customer.businessType === 'factory' ? '🏭' : '📦'} {customer.businessName}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="no-connections">No connections</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Flow Arrow Down */}
                              {(businessesByType.farm.length > 0 || businessesByType.fishing.length > 0) && businessesByType.factory.length > 0 && (
                                <div className="flow-arrow-section">
                                  <div className="flow-arrow">⬇️</div>
                                  <span className="flow-text">Raw Materials Flow</span>
                                </div>
                              )}

                              {/* Level 3: Factories */}
                              {businessesByType.factory.length > 0 && (
                                <div className="flow-level factories">
                                  <div className="level-header">
                                    <span className="level-badge">Level 3 - Processing</span>
                                  </div>
                                  <div className="businesses-row">
                                    {businessesByType.factory.map((business) => (
                                      <div key={business._id} className="business-flow-card factory">
                                        <div className="business-header">
                                          <span className="business-icon">🏭</span>
                                          <h6>{business.businessName}</h6>
                                        </div>
                                        <div className="business-stats">
                                          <div className="stat">
                                            <span className="label">Supplies:</span>
                                            <span className="value">{business.supplyLevel || 0}/{business.maxCapacity || 2000}</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">Accepted Farm:</span>
                                            <span className="value">{business.pricing?.acceptedFarmPrice ? `${business.pricing.acceptedFarmPrice} WR` : 'None'}</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">Warehouse Price:</span>
                                            <span className="value">{business.pricing?.sellToWarehousePrice || 20} WR</span>
                                          </div>
                                        </div>
                                        <div className="connections">
                                          <span className="connection-label">Receives from:</span>
                                          <div className="connection-list">
                                            {business.supplyChain?.connectedSuppliers?.length > 0 ? (
                                              business.supplyChain.connectedSuppliers.map((supplier) => (
                                                <span key={supplier.businessId} className="connection">
                                                  {supplier.businessType === 'farm' ? '🚜' : '🎣'} {supplier.businessName}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="no-connections">No suppliers</span>
                                            )}
                                          </div>
                                          <span className="connection-label">Supplies to:</span>
                                          <div className="connection-list">
                                            {business.supplyChain?.connectedCustomers?.length > 0 ? (
                                              business.supplyChain.connectedCustomers.map((customer) => (
                                                <span key={customer.businessId} className="connection">
                                                  🏬 {customer.businessName}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="no-connections">No customers</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Flow Arrow Down */}
                              {businessesByType.factory.length > 0 && businessesByType.warehouse.length > 0 && (
                                <div className="flow-arrow-section">
                                  <div className="flow-arrow">⬇️</div>
                                  <span className="flow-text">Processed Goods Flow</span>
                                </div>
                              )}

                              {/* Level 2: Warehouses */}
                              {businessesByType.warehouse.length > 0 && (
                                <div className="flow-level warehouses">
                                  <div className="level-header">
                                    <span className="level-badge">Level 2 - Distribution</span>
                                  </div>
                                  <div className="businesses-row">
                                    {businessesByType.warehouse.map((business) => (
                                      <div key={business._id} className="business-flow-card warehouse">
                                        <div className="business-header">
                                          <span className="business-icon">🏬</span>
                                          <h6>{business.businessName}</h6>
                                        </div>
                                        <div className="business-stats">
                                          <div className="stat">
                                            <span className="label">Supplies:</span>
                                            <span className="value">{business.supplyLevel || 0}/{business.maxCapacity || 1500}</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">Accepted Factory:</span>
                                            <span className="value">{business.pricing?.acceptedFactoryPrice ? `${business.pricing.acceptedFactoryPrice} WR` : 'None'}</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">Shop Price:</span>
                                            <span className="value">{business.pricing?.sellToShopPrice || 30} WR</span>
                                          </div>
                                        </div>
                                        <div className="connections">
                                          <span className="connection-label">Receives from:</span>
                                          <div className="connection-list">
                                            {business.supplyChain?.connectedSuppliers?.length > 0 ? (
                                              business.supplyChain.connectedSuppliers.map((supplier) => (
                                                <span key={supplier.businessId} className="connection">
                                                  🏭 {supplier.businessName}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="no-connections">No suppliers</span>
                                            )}
                                          </div>
                                          <span className="connection-label">Supplies to:</span>
                                          <div className="connection-list">
                                            {business.supplyChain?.connectedCustomers?.length > 0 ? (
                                              business.supplyChain.connectedCustomers.map((customer) => (
                                                <span key={customer.businessId} className="connection">
                                                  🏪 {customer.businessName}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="no-connections">No customers</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Flow Arrow Down */}
                              {businessesByType.warehouse.length > 0 && businessesByType.shop.length > 0 && (
                                <div className="flow-arrow-section">
                                  <div className="flow-arrow">⬇️</div>
                                  <span className="flow-text">Supply Distribution</span>
                                </div>
                              )}

                              {/* Level 1: Shops */}
                              {businessesByType.shop.length > 0 && (
                                <div className="flow-level shops">
                                  <div className="level-header">
                                    <span className="level-badge">Level 1 - Retail</span>
                                  </div>
                                  <div className="businesses-row">
                                    {businessesByType.shop.map((business) => (
                                      <div key={business._id} className="business-flow-card shop">
                                        <div className="business-header">
                                          <span className="business-icon">🏪</span>
                                          <h6>{business.businessName}</h6>
                                        </div>
                                        <div className="business-stats">
                                          <div className="stat">
                                            <span className="label">Supplies:</span>
                                            <span className="value">{business.supplyLevel || 0}/{business.maxCapacity || 250}</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">Accepted Warehouse:</span>
                                            <span className="value">{business.pricing?.acceptedWarehousePrice ? `${business.pricing.acceptedWarehousePrice} WR` : 'None'}</span>
                                          </div>
                                          <div className="stat">
                                            <span className="label">User Price:</span>
                                            <span className="value">{business.pricing?.sellToUsersPrice || 50} WR</span>
                                          </div>
                                        </div>
                                        <div className="connections">
                                          <span className="connection-label">Receives from:</span>
                                          <div className="connection-list">
                                            {business.supplyChain?.connectedSuppliers?.length > 0 ? (
                                              business.supplyChain.connectedSuppliers.map((supplier) => (
                                                <span key={supplier.businessId} className="connection">
                                                  🏬 {supplier.businessName}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="no-connections">No suppliers</span>
                                            )}
                                          </div>
                                          <span className="connection-label">Sells to:</span>
                                          <div className="connection-list">
                                            <span className="connection">👥 End Users</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Quick Stats Summary */}
                      <div className="network-summary">
                        <h6>📈 Network Summary</h6>
                        <div className="summary-stats">
                          <div className="summary-stat">
                            <span className="stat-icon">🔗</span>
                            <span className="stat-label">Total Businesses:</span>
                            <span className="stat-value">{userBusiness.length}</span>
                          </div>
                          <div className="summary-stat">
                            <span className="stat-icon">📦</span>
                            <span className="stat-label">Total Supplies:</span>
                            <span className="stat-value">{userBusiness.reduce((sum, b) => sum + (b.supplyLevel || 0), 0)}</span>
                          </div>
                          <div className="summary-stat">
                            <span className="stat-icon">💰</span>
                            <span className="stat-label">Connected Links:</span>
                            <span className="stat-value">{userBusiness.reduce((sum, b) => sum + (b.supplyChain?.connectedCustomers?.length || 0), 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-businesses">
                      <div className="empty-state">
                        <span className="empty-icon">🏗️</span>
                        <h5>No Businesses Created</h5>
                        <p>Create your first business to see your supply chain network here.</p>
                        <button 
                          onClick={() => setBusinessTab('create')}
                          style={{
                            background: 'linear-gradient(135deg, #00ff41, #28a745)',
                            color: '#1a1a1a',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            marginTop: '10px'
                          }}
                        >
                          🚀 Create Your First Business
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="requirements">
                    <h5>📋 Business Requirements:</h5>
                    <div className="requirements-grid">
                      <div className="requirement-item">
                        <span className="req-icon">🚜</span>
                        <span className="req-name">Farm</span>
                        <span className="req-stats">1,000 XP • 100,000 WR</span>
                      </div>
                      <div className="requirement-item">
                        <span className="req-icon">🏭</span>
                        <span className="req-name">Factory</span>
                        <span className="req-stats">1,500 XP • 150,000 WR</span>
                      </div>
                      <div className="requirement-item">
                        <span className="req-icon">🏬</span>
                        <span className="req-name">Warehouse</span>
                        <span className="req-stats">1,250 XP • 125,000 WR</span>
                      </div>
                      <div className="requirement-item">
                        <span className="req-icon">🏪</span>
                        <span className="req-name">Shop</span>
                        <span className="req-stats">2,000 XP • 200,000 WR</span>
                      </div>
                    </div>
                    <div className="user-stats">
                      <strong>Your Stats:</strong> {user?.xp?.toLocaleString() || 0} XP • {user?.wrCoins?.toLocaleString() || 0} WR coins
                    </div>
                  </div>

                  {/* Automatic Production Status */}
                  <div className="supply-chain-status">
                    <h5>⚡ Automatic Production System</h5>
                    <div className="status-card">
                      <div className="status-indicator">
                        <span className={`status-light ${supplyChainStatus?.isRunning ? 'active' : 'inactive'}`}></span>
                        <span className="status-text">
                          {supplyChainStatus?.isRunning ? 'ACTIVE - Producing every minute' : 'INACTIVE'}
                        </span>
                      </div>

                      {/* User-specific Auto Production Status */}
                      {supplyChainStatus?.userAutoProduction && (
                        <div className="user-auto-production-status">
                          <h6>🏭 Your Automatic Production Status</h6>
                          <div className={`user-status-indicator ${supplyChainStatus.userAutoProduction.qualifies ? 'qualified' : 'not-qualified'}`}>
                            <span className={`user-status-light ${supplyChainStatus.userAutoProduction.qualifies ? 'active' : 'inactive'}`}></span>
                            <span className="user-status-text">
                              {supplyChainStatus.userAutoProduction.qualifies ? 
                                '✅ QUALIFIED - Your businesses are automatically producing!' : 
                                '❌ NOT QUALIFIED - Requirements not met'
                              }
                            </span>
                          </div>
                          
                          <div className="user-requirements">
                            <p className="requirement-reason">
                              <strong>Status:</strong> {supplyChainStatus.userAutoProduction.reason}
                            </p>
                            
                            <div className="requirements-checklist">
                              <h7>📋 Requirements Check:</h7>
                              <ul className="checklist">
                                <li className={supplyChainStatus.userAutoProduction.requirements.hasExactlyFour ? 'requirement-met' : 'requirement-missing'}>
                                  {supplyChainStatus.userAutoProduction.requirements.hasExactlyFour ? '✅' : '❌'} 
                                  Exactly 4 businesses ({supplyChainStatus.userAutoProduction.requirements.businessCount}/4)
                                </li>
                                <li className={supplyChainStatus.userAutoProduction.requirements.hasShop ? 'requirement-met' : 'requirement-missing'}>
                                  {supplyChainStatus.userAutoProduction.requirements.hasShop ? '✅' : '❌'} 
                                  Shop business
                                </li>
                                <li className={supplyChainStatus.userAutoProduction.requirements.hasWarehouse ? 'requirement-met' : 'requirement-missing'}>
                                  {supplyChainStatus.userAutoProduction.requirements.hasWarehouse ? '✅' : '❌'} 
                                  Warehouse business
                                </li>
                                <li className={supplyChainStatus.userAutoProduction.requirements.hasFactory ? 'requirement-met' : 'requirement-missing'}>
                                  {supplyChainStatus.userAutoProduction.requirements.hasFactory ? '✅' : '❌'} 
                                  Factory business
                                </li>
                                <li className={supplyChainStatus.userAutoProduction.requirements.hasFarmOrFishing ? 'requirement-met' : 'requirement-missing'}>
                                  {supplyChainStatus.userAutoProduction.requirements.hasFarmOrFishing ? '✅' : '❌'} 
                                  Farm OR Fishing business (mandatory)
                                </li>
                              </ul>
                              
                              {supplyChainStatus.userAutoProduction.requirements.businessTypes.length > 0 && (
                                <p className="current-businesses">
                                  <strong>Your businesses:</strong> {supplyChainStatus.userAutoProduction.requirements.businessTypes.join(', ')}
                                </p>
                              )}
                            </div>

                            {supplyChainStatus.userAutoProduction.completeChainDetails && (
                              <div className="chain-details">
                                <h7>🔗 Complete Supply Chain Detected:</h7>
                                <p>✅ You have {supplyChainStatus.userAutoProduction.completeChainDetails.userBusinessesInChain} businesses in a {supplyChainStatus.userAutoProduction.completeChainDetails.chainBusinessCount}-business complete supply chain!</p>
                                <p><strong>Chain includes:</strong> {supplyChainStatus.userAutoProduction.completeChainDetails.businessTypes.join(', ')}</p>
                              </div>
                            )}
                            
                            {/* Enhanced 4-Business Chain Status Display */}
                            {supplyChainStatus.userAutoProduction.requirements.businessCount >= 4 && (
                              <div className="four-business-chain-status">
                                <h7>🏭 4-Business Chain Status:</h7>
                                <div className={`chain-status-indicator ${supplyChainStatus.userAutoProduction.qualifies ? 'chain-active' : 'chain-broken'}`}>
                                  <span className={`chain-status-light ${supplyChainStatus.userAutoProduction.qualifies ? 'active' : 'inactive'}`}></span>
                                  <span className="chain-status-text">
                                    {supplyChainStatus.userAutoProduction.qualifies ? 
                                      '🟢 CHAIN ACTIVE - All 4 businesses linked with automatic production' : 
                                      '🔴 CHAIN BROKEN - Price changes or missing links detected'
                                    }
                                  </span>
                                </div>
                                {!supplyChainStatus.userAutoProduction.qualifies && (
                                  <div className="chain-break-reason">
                                    <p><strong>Chain Issue:</strong> {supplyChainStatus.userAutoProduction.reason}</p>
                                    <p><em>💡 Tip: Check your business connections and ensure all pricing agreements are active. Any price change will break the automatic production until new agreements are made.</em></p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {supplyChainStatus?.stats && (
                        <div className="production-stats">
                          <div className="stat-row">
                            <span className="stat-label">🚜 Farms:</span>
                            <span className="stat-value">
                              {supplyChainStatus.stats.farm?.operational || 0}/{supplyChainStatus.stats.farm?.count || 0} operational
                              {supplyChainStatus.stats.farm?.totalSupply !== undefined && 
                                ` (${supplyChainStatus.stats.farm.totalSupply} supplies)`}
                            </span>
                          </div>
                          <div className="stat-row">
                            <span className="stat-label">🎣 Fishing:</span>
                            <span className="stat-value">
                              {supplyChainStatus.stats.fishing?.operational || 0}/{supplyChainStatus.stats.fishing?.count || 0} operational
                              {supplyChainStatus.stats.fishing?.totalSupply !== undefined && 
                                ` (${supplyChainStatus.stats.fishing.totalSupply} supplies)`}
                            </span>
                          </div>
                          <div className="stat-row">
                            <span className="stat-label">🏗️ Factories:</span>
                            <span className="stat-value">
                              {supplyChainStatus.stats.factory?.operational || 0}/{supplyChainStatus.stats.factory?.count || 0} operational
                              {supplyChainStatus.stats.factory?.totalSupply !== undefined && 
                                ` (${supplyChainStatus.stats.factory.totalSupply}/2000)`}
                            </span>
                          </div>
                          <div className="stat-row">
                            <span className="stat-label">🏭 Warehouses:</span>
                            <span className="stat-value">
                              {supplyChainStatus.stats.warehouse?.operational || 0}/{supplyChainStatus.stats.warehouse?.count || 0} operational
                              {supplyChainStatus.stats.warehouse?.totalSupply !== undefined && 
                                ` (${supplyChainStatus.stats.warehouse.totalSupply}/1500)`}
                            </span>
                          </div>
                          <div className="stat-row">
                            <span className="stat-label">🏪 Shops:</span>
                            <span className="stat-value">
                              {supplyChainStatus.stats.shop?.operational || 0}/{supplyChainStatus.stats.shop?.count || 0} operational
                              {supplyChainStatus.stats.shop?.totalSupply !== undefined && 
                                ` (${supplyChainStatus.stats.shop.totalSupply}/250)`}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="production-info">
                        <p>💡 <strong>How it works:</strong></p>
                        <ul>
                          <li>Farms/Fishing produce 1 supply every minute</li>
                          <li>Factories consume from farms and produce 1 supply per minute</li>
                          <li>Warehouses consume from factories and produce 1 supply per minute</li>
                          <li>Shops hold max 250, Warehouses 1500, Factories 2000 supplies</li>
                          <li>All businesses must be linked in the supply chain</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Outgoing Link Requests Section */}
                  {outgoingLinkRequests && outgoingLinkRequests.length > 0 && (
                    <div className="outgoing-requests-section" style={{ 
                      marginTop: '20px', 
                      padding: '15px', 
                      border: '1px solid rgba(0, 255, 65, 0.3)', 
                      borderRadius: '8px',
                      background: 'rgba(0, 0, 0, 0.2)'
                    }}>
                      <h5 style={{ color: '#00ff41', marginBottom: '15px' }}>📤 Your Pending Link Requests</h5>
                      <div className="outgoing-requests-list">
                        {outgoingLinkRequests.filter(request => request.status === 'pending').map((request) => (
                          <div key={request._id} className="outgoing-request-card" style={{
                            background: 'rgba(255, 193, 7, 0.1)', 
                            padding: '12px', 
                            borderRadius: '6px',
                            marginBottom: '10px',
                            border: '1px solid rgba(255, 193, 7, 0.3)'
                          }}>
                            <div className="request-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                              <span className="request-icon" style={{ fontSize: '20px', marginRight: '10px' }}>
                                {request.sourceBusinessType === 'farm' && '🚜'}
                                {request.sourceBusinessType === 'fishing' && '🎣'}
                                {request.sourceBusinessType === 'factory' && '🏗️'}
                                {request.sourceBusinessType === 'warehouse' && '🏭'}
                              </span>
                              <div className="request-details" style={{ flex: 1 }}>
                                <h6 style={{ margin: '0 0 5px 0', color: '#00ff41' }}>{request.sourceBusinessName} → {request.targetBusinessName}</h6>
                                <p style={{ margin: '2px 0', fontSize: '12px', color: '#ccc' }}>Proposed price: {request.proposedPrice} WR coins per supply</p>
                                <p style={{ margin: '2px 0', fontSize: '12px', color: '#ccc' }}>Target owner: {request.targetOwnerUsername}</p>
                                <p className="request-date" style={{ margin: '2px 0', fontSize: '11px', color: '#888' }}>
                                  Sent: {new Date(request.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="request-actions" style={{ textAlign: 'center', marginTop: '10px' }}>
                              <button
                                className="cancel-request-btn"
                                onClick={() => cancelBusinessLinkRequest(request._id)}
                                disabled={businessLoading}
                                style={{
                                  background: 'linear-gradient(45deg, #ff4757, #ff3742)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                              >
                                🚫 Cancel Request
                              </button>
                            </div>
                          </div>
                        ))}
                        
                        {/* Show non-pending requests for status */}
                        {outgoingLinkRequests.filter(request => request.status !== 'pending').map((request) => (
                          <div key={request._id} className={`outgoing-request-card ${request.status}`} style={{
                            background: request.status === 'accepted' ? 'rgba(40, 167, 69, 0.1)' : 
                                      request.status === 'rejected' ? 'rgba(220, 53, 69, 0.1)' : 
                                      'rgba(108, 117, 125, 0.1)', 
                            padding: '12px', 
                            borderRadius: '6px',
                            marginBottom: '10px',
                            border: request.status === 'accepted' ? '1px solid rgba(40, 167, 69, 0.3)' : 
                                   request.status === 'rejected' ? '1px solid rgba(220, 53, 69, 0.3)' : 
                                   '1px solid rgba(108, 117, 125, 0.3)'
                          }}>
                            <div className="request-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                              <span className="request-icon" style={{ fontSize: '20px', marginRight: '10px' }}>
                                {request.sourceBusinessType === 'farm' && '🚜'}
                                {request.sourceBusinessType === 'fishing' && '🎣'}
                                {request.sourceBusinessType === 'factory' && '🏗️'}
                                {request.sourceBusinessType === 'warehouse' && '🏭'}
                              </span>
                              <div className="request-details" style={{ flex: 1 }}>
                                <h6 style={{ margin: '0 0 5px 0', color: '#00ff41' }}>{request.sourceBusinessName} → {request.targetBusinessName}</h6>
                                <p style={{ margin: '2px 0', fontSize: '12px', color: '#ccc' }}>Proposed price: {request.proposedPrice} WR coins per supply</p>
                                <p style={{ margin: '2px 0', fontSize: '12px', color: '#ccc' }}>Target owner: {request.targetOwnerUsername}</p>
                                <p className="request-date" style={{ margin: '2px 0', fontSize: '11px', color: '#888' }}>
                                  Sent: {new Date(request.createdAt).toLocaleDateString()}
                                  {request.respondedAt && ` • ${request.status === 'accepted' ? 'Accepted' : request.status === 'rejected' ? 'Rejected' : 'Cancelled'}: ${new Date(request.respondedAt).toLocaleDateString()}`}
                                </p>
                              </div>
                            </div>
                            <div className="request-status" style={{ textAlign: 'center', marginTop: '10px' }}>
                              <span className={`status-badge ${request.status}`} style={{
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                background: request.status === 'accepted' ? 'rgba(40, 167, 69, 0.2)' : 
                                           request.status === 'rejected' ? 'rgba(220, 53, 69, 0.2)' : 
                                           'rgba(108, 117, 125, 0.2)',
                                color: request.status === 'accepted' ? '#28a745' : 
                                       request.status === 'rejected' ? '#dc3545' : 
                                       '#6c757d'
                              }}>
                                {request.status === 'accepted' && '✅ Accepted'}
                                {request.status === 'rejected' && '❌ Rejected'}
                                {request.status === 'cancelled' && '🚫 Cancelled'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {businessTab === 'create' && (
                <div className="business-creation">
                  <h4>🏗️ Create Your Business</h4>
                  <p style={{color: '#888', fontSize: '12px'}}>Debug: {businessTypes.length} business types loaded, User businesses: {userBusiness ? userBusiness.length : 'null'}</p>
                  
                  <div className="business-type-selection">
                    <h5>Select Business Type:</h5>
                    <div className="form-group">
                      <select
                        value={selectedBusinessType}
                        onChange={(e) => setSelectedBusinessType(e.target.value)}
                        className="business-type-select"
                      >
                        <option value="">Choose a business type...</option>
                        {businessTypes.map((type) => (
                          <option key={type.type} value={type.type}>
                            {type.icon} {type.name} (Tier {type.tier})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {selectedBusinessType && (
                      <div className="selected-business-details">
                        {(() => {
                          const selectedType = businessTypes.find(t => t.type === selectedBusinessType);
                          return selectedType ? (
                            <div className="business-type-card selected">
                              <div className="type-header">
                                <span className="icon">{selectedType.icon}</span>
                                <h6>{selectedType.name}</h6>
                                <span className="tier-badge">Tier {selectedType.tier}</span>
                              </div>
                              <p>{selectedType.description}</p>
                              <div className="business-info-section">
                                <h6>📋 Business Requirements:</h6>
                                <div className="requirement-item">
                                  <strong>Produces:</strong> {selectedType.produces}
                                </div>
                                <div className="requirement-item">
                                  <strong>Requires:</strong> {selectedType.requires}
                                </div>
                                <div className="requirement-item">
                                  <strong>Cost:</strong> {getBusinessRequirements(selectedType.type).cost.toLocaleString()} WR Coins
                                </div>
                                <div className="requirement-item">
                                  <strong>XP Required:</strong> {getBusinessRequirements(selectedType.type).xp.toLocaleString()} XP
                                </div>
                              </div>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>

                  {selectedBusinessType && (
                    <div className="business-form">
                      <div className="form-group">
                        <label>Business Name:</label>
                        <input
                          type="text"
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          placeholder="Enter your business name"
                          maxLength="50"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Description (Optional):</label>
                        <textarea
                          value={businessDescription}
                          onChange={(e) => setBusinessDescription(e.target.value)}
                          placeholder="Describe your business"
                          maxLength="200"
                          rows="3"
                        />
                      </div>

                      {selectedBusinessType && (
                        <>
                          <div className="cost-summary">
                            <h5>💰 Cost: {getBusinessRequirements(selectedBusinessType).cost.toLocaleString()} WR coins</h5>
                            <p>✨ Includes AI-generated business image</p>
                            <p>⚡ Instant business setup and registration</p>
                            <p>📈 XP Required: {getBusinessRequirements(selectedBusinessType).xp.toLocaleString()} XP</p>
                            {selectedBusinessType === 'fishing' && (
                              <p>🌊 <strong>Special:</strong> Can only be placed on water (sea/ocean)</p>
                            )}
                          </div>

                          {/* XP and Coins Requirements Check */}
                          {(user?.xp < getBusinessRequirements(selectedBusinessType).xp || user?.wrCoins < getBusinessRequirements(selectedBusinessType).cost) && (
                            <div className="requirements-warning">
                              {user?.xp < getBusinessRequirements(selectedBusinessType).xp && (
                                <p className="warning-text">
                                  ❌ You need {getBusinessRequirements(selectedBusinessType).xp.toLocaleString()} XP to create a {selectedBusinessType}. Current XP: {user?.xp || 0}
                                </p>
                              )}
                              {user?.wrCoins < getBusinessRequirements(selectedBusinessType).cost && (
                                <p className="warning-text">
                                  ❌ You need {getBusinessRequirements(selectedBusinessType).cost.toLocaleString()} WR Coins to create a {selectedBusinessType}. Current coins: {(user?.wrCoins || 0).toLocaleString()}
                                </p>
                              )}
                              <p className="tip-text">
                                💡 Complete tasks and challenges to earn XP and WR Coins!
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      <button 
                        className="create-business-btn"
                        onClick={handleCreateBusiness}
                        disabled={
                          businessLoading || 
                          !selectedBusinessType || 
                          !businessName.trim() || 
                          user?.xp < getBusinessRequirements(selectedBusinessType).xp || 
                          user?.wrCoins < getBusinessRequirements(selectedBusinessType).cost
                        }
                      >
                        {businessLoading ? 'Building Business...' : 'Build Business'}
                      </button>
                      
                      <div className="business-creation-info">
                        <p>📍 After building your business, you'll be taken to "Hack The Planet" to place it on the map!</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {businessTab === 'manage' && userBusiness && userBusiness.length > 0 && (() => {
                // Get the currently selected business
                const currentBusiness = userBusiness[selectedBusinessIndex] || userBusiness[0];
                
                return (
                <div className="business-management">
                  <h4>🔧 Manage Your Business</h4>
                  
                  <div className="business-info">
                    <div className="business-image">
                      {currentBusiness?.businessImageUrl ? (
                        <img 
                          src={currentBusiness.businessImageUrl.startsWith('http') ? currentBusiness.businessImageUrl : `${window.location.origin}${currentBusiness.businessImageUrl}`} 
                          alt={currentBusiness.businessName}
                          onError={(e) => {
                            // First try SVG fallback, then hide image
                            if (e.target.src.includes('default-business.svg')) {
                              e.target.style.display = 'none';
                              if (e.target.nextElementSibling) {
                                e.target.nextElementSibling.style.display = 'flex';
                              }
                            } else {
                              e.target.src = '/images/default-business.svg';
                            }
                          }}
                        />
                      ) : (
                        <div className="no-image">🏪 No Image</div>
                      )}
                      <button 
                        className="regenerate-image-btn"
                        onClick={handleRegenerateBusinessImage}
                        disabled={businessLoading}
                      >
                        {businessLoading ? 'Generating...' : '🔄 Regenerate Image'}
                      </button>
                    </div>
                    
                    <div className="business-details">
                      <h5>{currentBusiness?.businessName}</h5>
                      <p><strong>Type:</strong> {currentBusiness?.businessType}</p>
                      {currentBusiness?.description && (
                        <p><strong>Description:</strong> {currentBusiness.description}</p>
                      )}
                      <p><strong>Status:</strong> {currentBusiness?.operationalStatus}</p>
                      <p><strong>Supply Level:</strong> {currentBusiness?.supplyLevel} / {currentBusiness?.maxCapacity}</p>
                      
                      <div className="supply-chain-info">
                        <h6>Supply Chain:</h6>
                        <p><strong>Produces:</strong> {currentBusiness?.supplyChain?.produces}</p>
                        <p><strong>Requires:</strong> {currentBusiness?.supplyChain?.requires}</p>
                        <p><strong>Sells to:</strong> {currentBusiness?.supplyChain?.sellsTo?.join(', ')}</p>
                        <p><strong>Buys from:</strong> {currentBusiness?.supplyChain?.buysFrom?.join(', ')}</p>
                      </div>
                      
                      {/* Supply Chain Connections */}
                      <div className="supply-chain-connections">
                        <h6>🔗 Supply Chain Connections:</h6>
                        
                        {/* Connected Suppliers */}
                        {currentBusiness?.supplyChain?.connectedSuppliers?.length > 0 && (
                          <div className="connections-section">
                            <h7>📦 Suppliers ({currentBusiness.supplyChain.connectedSuppliers.length}):</h7>
                            {currentBusiness.supplyChain.connectedSuppliers.map((supplier, index) => (
                              <div key={index} className="connection-item" style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                margin: '5px 0',
                                background: 'rgba(0, 255, 65, 0.1)',
                                borderRadius: '5px',
                                border: '1px solid rgba(0, 255, 65, 0.3)'
                              }}>
                                <div className="connection-info" style={{ flex: 1 }}>
                                  <div className="business-name" style={{
                                    fontWeight: 'bold',
                                    color: '#00ff41',
                                    fontSize: '13px'
                                  }}>{supplier.businessName}</div>
                                  <div className="business-details" style={{
                                    fontSize: '11px',
                                    color: '#888',
                                    marginTop: '2px'
                                  }}>
                                    {supplier.businessType} • {supplier.ownerUsername} • {(supplier.distance/1000).toFixed(1)}km
                                  </div>
                                </div>
                                <button 
                                  className="unlink-btn"
                                  onClick={() => handleUnlinkBusiness(supplier.businessId, supplier.businessName, 'supplier')}
                                  style={{
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    padding: '4px 8px',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontSize: '11px'
                                  }}
                                >
                                  🗑️ Unlink
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Connected Customers */}
                        {currentBusiness?.supplyChain?.connectedCustomers?.length > 0 && (
                          <div className="connections-section">
                            <h7>🏪 Customers ({currentBusiness.supplyChain.connectedCustomers.length}):</h7>
                            {currentBusiness.supplyChain.connectedCustomers.map((customer, index) => (
                              <div key={index} className="connection-item" style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                margin: '5px 0',
                                background: 'rgba(100, 150, 255, 0.1)',
                                borderRadius: '5px',
                                border: '1px solid rgba(100, 150, 255, 0.3)'
                              }}>
                                <div className="connection-info" style={{ flex: 1 }}>
                                  <div className="business-name" style={{
                                    fontWeight: 'bold',
                                    color: '#6496ff',
                                    fontSize: '13px'
                                  }}>{customer.businessName}</div>
                                  <div className="business-details" style={{
                                    fontSize: '11px',
                                    color: '#888',
                                    marginTop: '2px'
                                  }}>
                                    {customer.businessType} • {customer.ownerUsername} • {(customer.distance/1000).toFixed(1)}km
                                  </div>
                                </div>
                                <span className="connection-note" style={{ fontSize: '11px', color: '#888' }}>
                                  (Customer relationship - managed by them)
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {(!currentBusiness?.supplyChain?.connectedSuppliers?.length && !currentBusiness?.supplyChain?.connectedCustomers?.length) && (
                          <p style={{ fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                            No supply chain connections yet. Visit "Hack The Planet" to link with other businesses within 10km.
                          </p>
                        )}
                      </div>
                      
                      {/* Business Location Status */}
                      <div className="business-location-status">
                        <h6>📍 Location Status:</h6>
                        {(() => {
                          const isPlaced = currentBusiness?.location && 
                                          currentBusiness.location.lat && 
                                          currentBusiness.location.lng &&
                                          currentBusiness.location.lat !== 0 && 
                                          currentBusiness.location.lng !== 0;
                          return isPlaced;
                        })() ? (
                          <div className="location-placed">
                            <p style={{ color: '#00ff41', fontWeight: 'bold' }}>
                              ✅ Placed on map at coordinates: {(currentBusiness.location.lat || 0).toFixed(4)}, {(currentBusiness.location.lng || 0).toFixed(4)}
                            </p>
                            <p style={{ fontSize: '12px', color: '#888' }}>
                              Your business is visible to all users on "Hack The Planet"
                            </p>
                          </div>
                        ) : (
                          <div className="location-unplaced">
                            <p style={{ color: '#ff6b35', fontWeight: 'bold' }}>
                              ⚠️ Business not placed on map yet
                            </p>
                            <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>
                              Other users cannot see your business until it's placed on the map
                            </p>
                            <button 
                              className="place-business-btn"
                              onClick={() => navigate('/hack-the-planet')}
                              style={{
                                background: '#00ff41',
                                color: 'black',
                                border: 'none',
                                padding: '10px 15px',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                width: '100%'
                              }}
                            >
                              📍 Place Business on Map
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Business Operations */}
                      {(currentBusiness?.businessType === 'farm' || currentBusiness?.businessType === 'fishing') && (
                        <div className="business-operations" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #333' }}>
                          <h6>🏭 Production Operations:</h6>
                          <button 
                            onClick={handleGenerateSupplies}
                            disabled={businessLoading}
                            style={{
                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #28a745, #20c997)',
                              color: 'white',
                              border: 'none',
                              padding: '10px 15px',
                              borderRadius: '5px',
                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              width: '100%',
                              marginTop: '10px'
                            }}
                          >
                            {businessLoading ? '⏳ Generating...' : '🔄 Generate Supplies'}
                          </button>
                          <p style={{ fontSize: '11px', color: '#888', marginTop: '5px' }}>
                            Generate supplies for your {currentBusiness?.businessType}. Can be done once per hour.
                          </p>
                        </div>
                      )}

                      {/* Supply Chain Purchasing */}
                      {currentBusiness?.supplyChain?.connectedSuppliers?.length > 0 && (
                        <div className="supply-purchasing" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #333' }}>
                          <h6>🛒 Purchase from Suppliers:</h6>
                          {currentBusiness.supplyChain.connectedSuppliers.map((supplier) => (
                            <div key={supplier.businessId} style={{ 
                              background: 'rgba(0, 255, 65, 0.1)', 
                              padding: '12px', 
                              borderRadius: '6px', 
                              marginTop: '10px',
                              border: '1px solid rgba(0, 255, 65, 0.3)'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontWeight: 'bold', color: '#00ff41' }}>
                                  {supplier.businessName} ({supplier.businessType})
                                </span>
                                <span style={{ fontSize: '12px', color: '#888' }}>
                                  {(supplier.distance / 1000).toFixed(1)}km away
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <label style={{ fontSize: '12px', color: '#00ff41' }}>Amount:</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="100"
                                  defaultValue="10"
                                  id={`purchase-amount-${supplier.businessId}`}
                                  style={{
                                    width: '60px',
                                    padding: '4px',
                                    fontSize: '12px',
                                    background: '#1a1a1a',
                                    color: '#00ff41',
                                    border: '1px solid #00ff41',
                                    borderRadius: '3px'
                                  }}
                                />
                                <span style={{ fontSize: '11px', color: '#888' }}>
                                  @ {supplier.pricing?.businessSellPrice || 15} WR coins each
                                </span>
                              </div>
                              <button
                                onClick={() => handleAcceptUpstreamPricing(supplier.businessId)}
                                disabled={businessLoading}
                                style={{
                                  background: businessLoading ? '#666' : 'linear-gradient(135deg, #28a745, #20c997)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: businessLoading ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  width: '100%'
                                }}
                              >
                                {businessLoading ? '⏳ Accepting...' : '✅ Accept Price & Generate'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Business-Specific Pricing Management */}
                      {currentBusiness && (
                        <div className="business-pricing" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #333' }}>
                          {/* FARM PRICING */}
                          {currentBusiness.businessType === 'farm' && (
                            <div>
                              <h6>💰 Farm Pricing:</h6>
                              
                              {/* Sell to Factory Price */}
                              <div style={{ 
                                background: 'rgba(0, 255, 65, 0.1)', 
                                padding: '12px', 
                                borderRadius: '6px', 
                                border: '1px solid rgba(0, 255, 65, 0.3)',
                                marginBottom: '15px'
                              }}>
                                <h7 style={{ color: '#00ff41', fontSize: '14px', fontWeight: 'bold' }}>🏭 Sell to Factory:</h7>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#888', minWidth: '80px' }}>Current:</span>
                                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#00ff41' }}>
                                    {currentBusiness?.pricing?.sellToFactoryPrice || 15} WR coins
                                  </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    max="1000"
                                    defaultValue={currentBusiness?.pricing?.sellToFactoryPrice || 15}
                                    id="farm-factory-price-input"
                                    style={{
                                      width: '80px',
                                      padding: '6px',
                                      fontSize: '12px',
                                      background: '#1a1a1a',
                                      color: '#00ff41',
                                      border: '1px solid #00ff41',
                                      borderRadius: '3px'
                                    }}
                                  />
                                  <button
                                    onClick={async () => await updateSpecificPrice('sellToFactoryPrice', 'farm-factory-price-input')}
                                    disabled={businessLoading}
                                    style={{
                                      background: businessLoading ? '#666' : 'linear-gradient(135deg, #00ff41, #28a745)',
                                      color: '#1a1a1a',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      cursor: businessLoading ? 'not-allowed' : 'pointer',
                                      fontSize: '11px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {businessLoading ? '⏳' : '💰 Update'}
                                  </button>
                                </div>
                              </div>

                              {/* Sell to Owner Price */}
                              <div style={{ 
                                background: 'rgba(255, 193, 7, 0.1)', 
                                padding: '12px', 
                                borderRadius: '6px', 
                                border: '1px solid rgba(255, 193, 7, 0.3)',
                                marginBottom: '15px'
                              }}>
                                <h7 style={{ color: '#ffc107', fontSize: '14px', fontWeight: 'bold' }}>👤 Sell to Owner (You):</h7>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#888', minWidth: '80px' }}>Current:</span>
                                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#ffc107' }}>
                                    {currentBusiness?.pricing?.sellToOwnerPrice || 25} WR coins
                                  </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    max="1000"
                                    defaultValue={currentBusiness?.pricing?.sellToOwnerPrice || 25}
                                    id="farm-owner-price-input"
                                    style={{
                                      width: '80px',
                                      padding: '6px',
                                      fontSize: '12px',
                                      background: '#1a1a1a',
                                      color: '#ffc107',
                                      border: '1px solid #ffc107',
                                      borderRadius: '3px'
                                    }}
                                  />
                                  <button
                                    onClick={async () => await updateSpecificPrice('sellToOwnerPrice', 'farm-owner-price-input')}
                                    disabled={businessLoading}
                                    style={{
                                      background: businessLoading ? '#666' : 'linear-gradient(135deg, #ffc107, #ff8c00)',
                                      color: '#1a1a1a',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      cursor: businessLoading ? 'not-allowed' : 'pointer',
                                      fontSize: '11px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {businessLoading ? '⏳' : '💰 Update'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* FACTORY PRICING */}
                          {currentBusiness.businessType === 'factory' && (
                            <div>
                              <h6>💰 Factory Pricing:</h6>
                              
                              {/* Accept Farm Price */}
                              <div style={{ 
                                background: 'rgba(255, 99, 132, 0.1)', 
                                padding: '12px', 
                                borderRadius: '6px', 
                                border: '1px solid rgba(255, 99, 132, 0.3)',
                                marginBottom: '15px'
                              }}>
                                <h7 style={{ color: '#ff6384', fontSize: '14px', fontWeight: 'bold' }}>📥 Accept Farm Price:</h7>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#888' }}>
                                    {currentBusiness?.pricing?.acceptedFarmPrice ? 
                                      `Accepted: ${currentBusiness.pricing.acceptedFarmPrice} WR coins` : 
                                      'No price accepted yet'
                                    }
                                  </span>
                                </div>
                                
                                {/* Show pending link requests from farms */}
                                {(() => {
                                  const filteredRequests = businessLinkRequests.filter(request => 
                                    request.targetBusinessId === currentBusiness?._id && 
                                    request.status === 'pending' &&
                                    ['farm', 'fishing'].includes(request.sourceBusinessType)
                                  );
                                  console.log(`DEBUG: Factory ${currentBusiness?.businessName} (${currentBusiness?._id})`);
                                  console.log(`DEBUG: All businessLinkRequests:`, businessLinkRequests.length);
                                  console.log(`DEBUG: Filtered farm/fishing requests:`, filteredRequests.length);
                                  console.log(`DEBUG: Sample requests:`, businessLinkRequests.slice(0, 2).map(r => ({
                                    targetId: r.targetBusinessId,
                                    currentId: currentBusiness?._id,
                                    idMatch: r.targetBusinessId === currentBusiness?._id,
                                    idTypes: `${typeof r.targetBusinessId} vs ${typeof currentBusiness?._id}`,
                                    status: r.status,
                                    sourceType: r.sourceBusinessType,
                                    sourceName: r.sourceBusinessName
                                  })));
                                  return filteredRequests.length > 0;
                                })() && (
                                  <div style={{ marginTop: '10px' }}>
                                    <div style={{ fontSize: '12px', color: '#ff6384', fontWeight: 'bold', marginBottom: '8px' }}>
                                      📋 Pending Link Requests:
                                    </div>
                                    {businessLinkRequests
                                      .filter(request => 
                                        request.targetBusinessId === currentBusiness?._id && 
                                        request.status === 'pending' &&
                                        ['farm', 'fishing'].includes(request.sourceBusinessType)
                                      )
                                      .map((request) => (
                                      <div key={request._id} style={{ 
                                        background: 'rgba(255, 193, 7, 0.1)', 
                                        padding: '8px', 
                                        borderRadius: '4px',
                                        marginBottom: '5px',
                                        border: '1px solid rgba(255, 193, 7, 0.3)'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                          <span style={{ fontSize: '11px', color: '#888' }}>
                                            🚜 {request.sourceBusinessName} wants to link as supplier
                                          </span>
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#888', marginBottom: '8px' }}>
                                          Proposed price: {request.proposedPrice} WR coins • Owner: {request.sourceOwnerUsername}
                                        </div>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                          <button
                                            onClick={() => acceptBusinessLinkRequest(request._id)}
                                            disabled={businessLoading}
                                            style={{
                                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #28a745, #20c997)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            ✅ Accept
                                          </button>
                                          <button
                                            onClick={() => rejectBusinessLinkRequest(request._id)}
                                            disabled={businessLoading}
                                            style={{
                                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #dc3545, #c82333)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            ❌ Reject
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Show connected suppliers */}
                                {currentBusiness?.supplyChain?.connectedSuppliers?.length > 0 && (
                                  <div style={{ marginTop: '10px' }}>
                                    {currentBusiness.supplyChain.connectedSuppliers
                                      .filter(supplier => ['farm', 'fishing'].includes(supplier.businessType))
                                      .map((supplier) => (
                                      <div key={supplier.businessId} style={{ 
                                        background: 'rgba(0, 255, 65, 0.1)', 
                                        padding: '8px', 
                                        borderRadius: '4px',
                                        marginBottom: '5px',
                                        border: '1px solid rgba(0, 255, 65, 0.2)'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span style={{ fontSize: '11px', color: '#888' }}>
                                            {supplier.businessName} ({supplier.businessType}): {supplier.pricing?.sellToFactoryPrice || 15} WR coins
                                          </span>
                                          <button
                                            onClick={() => acceptSupplierPrice(supplier.businessId)}
                                            disabled={businessLoading}
                                            style={{
                                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #ff6384, #e91e63)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            {businessLoading ? '⏳' : '✅ Accept'}
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Sell to Warehouse Price */}
                              <div style={{ 
                                background: 'rgba(54, 162, 235, 0.1)', 
                                padding: '12px', 
                                borderRadius: '6px', 
                                border: '1px solid rgba(54, 162, 235, 0.3)',
                                marginBottom: '15px'
                              }}>
                                <h7 style={{ color: '#36a2eb', fontSize: '14px', fontWeight: 'bold' }}>🏬 Sell to Warehouse:</h7>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#888', minWidth: '80px' }}>Current:</span>
                                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#36a2eb' }}>
                                    {currentBusiness?.pricing?.sellToWarehousePrice || 20} WR coins
                                  </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    max="1000"
                                    defaultValue={currentBusiness?.pricing?.sellToWarehousePrice || 20}
                                    id="factory-warehouse-price-input"
                                    style={{
                                      width: '80px',
                                      padding: '6px',
                                      fontSize: '12px',
                                      background: '#1a1a1a',
                                      color: '#36a2eb',
                                      border: '1px solid #36a2eb',
                                      borderRadius: '3px'
                                    }}
                                  />
                                  <button
                                    onClick={async () => await updateSpecificPrice('sellToWarehousePrice', 'factory-warehouse-price-input')}
                                    disabled={businessLoading}
                                    style={{
                                      background: businessLoading ? '#666' : 'linear-gradient(135deg, #36a2eb, #007bff)',
                                      color: 'white',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      cursor: businessLoading ? 'not-allowed' : 'pointer',
                                      fontSize: '11px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {businessLoading ? '⏳' : '💰 Update'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* WAREHOUSE PRICING */}
                          {currentBusiness.businessType === 'warehouse' && (
                            <div>
                              <h6>💰 Warehouse Pricing:</h6>
                              
                              {/* Accept Factory Price */}
                              <div style={{ 
                                background: 'rgba(255, 99, 132, 0.1)', 
                                padding: '12px', 
                                borderRadius: '6px', 
                                border: '1px solid rgba(255, 99, 132, 0.3)',
                                marginBottom: '15px'
                              }}>
                                <h7 style={{ color: '#ff6384', fontSize: '14px', fontWeight: 'bold' }}>📥 Accept Factory Price:</h7>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#888' }}>
                                    {currentBusiness?.pricing?.acceptedFactoryPrice ? 
                                      `Accepted: ${currentBusiness.pricing.acceptedFactoryPrice} WR coins` : 
                                      'No price accepted yet'
                                    }
                                  </span>
                                </div>
                                
                                {/* Show pending link requests from factories */}
                                {businessLinkRequests.filter(request => 
                                  request.targetBusinessId === currentBusiness?._id && 
                                  request.status === 'pending' &&
                                  request.sourceBusinessType === 'factory'
                                ).length > 0 && (
                                  <div style={{ marginTop: '10px' }}>
                                    <div style={{ fontSize: '12px', color: '#ff6384', fontWeight: 'bold', marginBottom: '8px' }}>
                                      📋 Pending Link Requests:
                                    </div>
                                    {businessLinkRequests
                                      .filter(request => 
                                        request.targetBusinessId === currentBusiness?._id && 
                                        request.status === 'pending' &&
                                        request.sourceBusinessType === 'factory'
                                      )
                                      .map((request) => (
                                      <div key={request._id} style={{ 
                                        background: 'rgba(255, 193, 7, 0.1)', 
                                        padding: '8px', 
                                        borderRadius: '4px',
                                        marginBottom: '5px',
                                        border: '1px solid rgba(255, 193, 7, 0.3)'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                          <span style={{ fontSize: '11px', color: '#888' }}>
                                            🏗️ {request.sourceBusinessName} wants to link as supplier
                                          </span>
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#888', marginBottom: '8px' }}>
                                          Proposed price: {request.proposedPrice} WR coins • Owner: {request.sourceOwnerUsername}
                                        </div>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                          <button
                                            onClick={() => acceptBusinessLinkRequest(request._id)}
                                            disabled={businessLoading}
                                            style={{
                                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #28a745, #20c997)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            ✅ Accept
                                          </button>
                                          <button
                                            onClick={() => rejectBusinessLinkRequest(request._id)}
                                            disabled={businessLoading}
                                            style={{
                                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #dc3545, #c82333)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            ❌ Reject
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Show connected suppliers */}
                                {currentBusiness?.supplyChain?.connectedSuppliers?.length > 0 && (
                                  <div style={{ marginTop: '10px' }}>
                                    {currentBusiness.supplyChain.connectedSuppliers
                                      .filter(supplier => supplier.businessType === 'factory')
                                      .map((supplier) => (
                                      <div key={supplier.businessId} style={{ 
                                        background: 'rgba(54, 162, 235, 0.1)', 
                                        padding: '8px', 
                                        borderRadius: '4px',
                                        marginBottom: '5px',
                                        border: '1px solid rgba(54, 162, 235, 0.2)'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span style={{ fontSize: '11px', color: '#888' }}>
                                            {supplier.businessName} (factory): {supplier.pricing?.sellToWarehousePrice || 20} WR coins
                                          </span>
                                          <button
                                            onClick={() => acceptSupplierPrice(supplier.businessId)}
                                            disabled={businessLoading}
                                            style={{
                                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #ff6384, #e91e63)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            {businessLoading ? '⏳' : '✅ Accept'}
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Sell to Shop Price */}
                              <div style={{ 
                                background: 'rgba(153, 102, 255, 0.1)', 
                                padding: '12px', 
                                borderRadius: '6px', 
                                border: '1px solid rgba(153, 102, 255, 0.3)',
                                marginBottom: '15px'
                              }}>
                                <h7 style={{ color: '#9966ff', fontSize: '14px', fontWeight: 'bold' }}>🏪 Sell to Shop:</h7>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#888', minWidth: '80px' }}>Current:</span>
                                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#9966ff' }}>
                                    {currentBusiness?.pricing?.sellToShopPrice || 30} WR coins
                                  </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    max="1000"
                                    defaultValue={currentBusiness?.pricing?.sellToShopPrice || 30}
                                    id="warehouse-shop-price-input"
                                    style={{
                                      width: '80px',
                                      padding: '6px',
                                      fontSize: '12px',
                                      background: '#1a1a1a',
                                      color: '#9966ff',
                                      border: '1px solid #9966ff',
                                      borderRadius: '3px'
                                    }}
                                  />
                                  <button
                                    onClick={async () => await updateSpecificPrice('sellToShopPrice', 'warehouse-shop-price-input')}
                                    disabled={businessLoading}
                                    style={{
                                      background: businessLoading ? '#666' : 'linear-gradient(135deg, #9966ff, #7c3aed)',
                                      color: 'white',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      cursor: businessLoading ? 'not-allowed' : 'pointer',
                                      fontSize: '11px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {businessLoading ? '⏳' : '💰 Update'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* SHOP PRICING */}
                          {currentBusiness.businessType === 'shop' && (
                            <div>
                              <h6>💰 Shop Pricing:</h6>
                              
                              {/* Accept Warehouse Price */}
                              <div style={{ 
                                background: 'rgba(255, 99, 132, 0.1)', 
                                padding: '12px', 
                                borderRadius: '6px', 
                                border: '1px solid rgba(255, 99, 132, 0.3)',
                                marginBottom: '15px'
                              }}>
                                <h7 style={{ color: '#ff6384', fontSize: '14px', fontWeight: 'bold' }}>📥 Accept Warehouse Price:</h7>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#888' }}>
                                    {currentBusiness?.pricing?.acceptedWarehousePrice ? 
                                      `Accepted: ${currentBusiness.pricing.acceptedWarehousePrice} WR coins` : 
                                      'No price accepted yet'
                                    }
                                  </span>
                                </div>
                                
                                {/* Show pending link requests from warehouses */}
                                {businessLinkRequests.filter(request => 
                                  request.targetBusinessId === currentBusiness?._id && 
                                  request.status === 'pending' &&
                                  request.sourceBusinessType === 'warehouse'
                                ).length > 0 && (
                                  <div style={{ marginTop: '10px' }}>
                                    <div style={{ fontSize: '12px', color: '#ff6384', fontWeight: 'bold', marginBottom: '8px' }}>
                                      📋 Pending Link Requests:
                                    </div>
                                    {businessLinkRequests
                                      .filter(request => 
                                        request.targetBusinessId === currentBusiness?._id && 
                                        request.status === 'pending' &&
                                        request.sourceBusinessType === 'warehouse'
                                      )
                                      .map((request) => (
                                      <div key={request._id} style={{ 
                                        background: 'rgba(255, 193, 7, 0.1)', 
                                        padding: '8px', 
                                        borderRadius: '4px',
                                        marginBottom: '5px',
                                        border: '1px solid rgba(255, 193, 7, 0.3)'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                          <span style={{ fontSize: '11px', color: '#888' }}>
                                            🏭 {request.sourceBusinessName} wants to link as supplier
                                          </span>
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#888', marginBottom: '8px' }}>
                                          Proposed price: {request.proposedPrice} WR coins • Owner: {request.sourceOwnerUsername}
                                        </div>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                          <button
                                            onClick={() => acceptBusinessLinkRequest(request._id)}
                                            disabled={businessLoading}
                                            style={{
                                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #28a745, #20c997)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            ✅ Accept
                                          </button>
                                          <button
                                            onClick={() => rejectBusinessLinkRequest(request._id)}
                                            disabled={businessLoading}
                                            style={{
                                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #dc3545, #c82333)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            ❌ Reject
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Show connected suppliers */}
                                {currentBusiness?.supplyChain?.connectedSuppliers?.length > 0 && (
                                  <div style={{ marginTop: '10px' }}>
                                    {currentBusiness.supplyChain.connectedSuppliers
                                      .filter(supplier => supplier.businessType === 'warehouse')
                                      .map((supplier) => (
                                      <div key={supplier.businessId} style={{ 
                                        background: 'rgba(153, 102, 255, 0.1)', 
                                        padding: '8px', 
                                        borderRadius: '4px',
                                        marginBottom: '5px',
                                        border: '1px solid rgba(153, 102, 255, 0.2)'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span style={{ fontSize: '11px', color: '#888' }}>
                                            {supplier.businessName} (warehouse): {supplier.pricing?.sellToShopPrice || 30} WR coins
                                          </span>
                                          <button
                                            onClick={() => acceptSupplierPrice(supplier.businessId)}
                                            disabled={businessLoading}
                                            style={{
                                              background: businessLoading ? '#666' : 'linear-gradient(135deg, #ff6384, #e91e63)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '4px 8px',
                                              borderRadius: '3px',
                                              cursor: businessLoading ? 'not-allowed' : 'pointer',
                                              fontSize: '10px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            {businessLoading ? '⏳' : '✅ Accept'}
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Sell to Users Price */}
                              <div style={{ 
                                background: 'rgba(255, 206, 84, 0.1)', 
                                padding: '12px', 
                                borderRadius: '6px', 
                                border: '1px solid rgba(255, 206, 84, 0.3)',
                                marginBottom: '15px'
                              }}>
                                <h7 style={{ color: '#ffce54', fontSize: '14px', fontWeight: 'bold' }}>👥 Sell to Users:</h7>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#888', minWidth: '80px' }}>Current:</span>
                                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#ffce54' }}>
                                    {currentBusiness?.pricing?.sellToUsersPrice || 50} WR coins
                                  </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    max="1000"
                                    defaultValue={currentBusiness?.pricing?.sellToUsersPrice || 50}
                                    id="shop-users-price-input"
                                    style={{
                                      width: '80px',
                                      padding: '6px',
                                      fontSize: '12px',
                                      background: '#1a1a1a',
                                      color: '#ffce54',
                                      border: '1px solid #ffce54',
                                      borderRadius: '3px'
                                    }}
                                  />
                                  <button
                                    onClick={async () => await updateSpecificPrice('sellToUsersPrice', 'shop-users-price-input')}
                                    disabled={businessLoading}
                                    style={{
                                      background: businessLoading ? '#666' : 'linear-gradient(135deg, #ffce54, #f39c12)',
                                      color: '#1a1a1a',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      cursor: businessLoading ? 'not-allowed' : 'pointer',
                                      fontSize: '11px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {businessLoading ? '⏳' : '💰 Update'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Business Management Actions */}
                      <div className="business-actions" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #333' }}>
                        <h6>⚙️ Business Management:</h6>
                        <button 
                          onClick={() => handleDeleteBusiness(currentBusiness._id, currentBusiness.businessName)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '10px 15px',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            width: '100%',
                            marginTop: '10px'
                          }}
                        >
                          🗑️ Delete Business
                        </button>
                        <p style={{ fontSize: '11px', color: '#888', marginTop: '5px' }}>
                          ⚠️ Warning: This action cannot be undone. All supply chain connections will be removed.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })()}

              {businessError && (
                <div className="error-message">
                  <p>❌ {businessError}</p>
                  <button onClick={fetchBusinessData}>🔄 Retry</button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'career' && (
          <div className="tab-content">
            <div className="career-section">
              <div className="section-header">
                <h3>💼 Cybersecurity Career Center</h3>
                <p>Start your cybersecurity career with global companies. Work in major cities worldwide and earn steady income!</p>
              </div>

              {careerLoading ? (
                <div className="loading-state">
                  <p>🔄 Loading career information...</p>
                </div>
              ) : careerError ? (
                <div className="error-message">
                  <p>❌ {careerError}</p>
                  <button onClick={fetchCareerData}>🔄 Retry</button>
                </div>
              ) : (
                <div className="career-content">
                  {/* Current Job Status */}
                  {careerData.userJob ? (
                    <div className="current-job-section">
                      <h4>🏢 Current Employment</h4>
                      <div className="job-card current-job">
                        <div className="job-header">
                          <h5>{
                            careerData.userJob.jobLevel === 'junior' ? '🔰 Junior Network Security Officer' : 
                            careerData.userJob.jobLevel === 'senior' ? '⭐ Network Security Administrator' :
                            careerData.userJob.jobLevel === 'fieldAgent' ? '🤵 Field Agent' : 
                            careerData.userJob.jobTitle
                          }</h5>
                          <span className="job-salary">{
                            careerData.userJob.jobLevel === 'junior' ? '18 WR/hour' : 
                            careerData.userJob.jobLevel === 'senior' ? '28 WR/hour' :
                            careerData.userJob.jobLevel === 'fieldAgent' ? '35 WR/hour' :
                            careerData.userJob.hourlyRate + ' WR/hour'
                          }</span>
                        </div>
                        <div className="job-details">
                          <p><strong>🏢 Company:</strong> {careerData.userJob.company}</p>
                          <p><strong>📍 Location:</strong> {careerData.userJob.officeName}</p>
                          <p><strong>🕒 Schedule:</strong> Monday-Thursday, 8-hour shifts</p>
                          <p><strong>📊 Attendance:</strong> {careerData.userJob.attendanceStreak} consecutive days</p>
                          
                          {/* Field Agent Special Abilities */}
                          {careerData.userJob.jobLevel === 'fieldAgent' && (
                            <div className="field-agent-abilities">
                              <h6>🤵 Field Agent Abilities</h6>
                              <ul>
                                <li>🚗 10 computer moves per 6-hour period (same as hackers)</li>
                                <li>📡 Fixed 2km sniffer range (government equipment)</li>
                                <li>🔒 Enhanced computer security (90% protection against hacks)</li>
                                <li>👮 Authority to arrest users and send them to jail</li>
                                <li>🛒 Can purchase from shops and farms like regular users</li>
                              </ul>
                            </div>
                          )}
                        </div>
                        
                        {/* Work Session Status for regular employees */}
                        {careerData.userJob.jobLevel !== 'fieldAgent' && careerData.workSession ? (
                          <div className="work-session">
                            <h6>🔄 Currently Working</h6>
                            <div className="work-progress">
                              <div className="progress-bar">
                                <div 
                                  className="progress-fill"
                                  style={{ width: `${(careerData.workSession.progress / 8) * 100}%` }}
                                ></div>
                              </div>
                              <p>{careerData.workSession.progress.toFixed(1)} / 8.0 hours completed</p>
                              <p><strong>💰 Earnings:</strong> {careerData.workSession.earnings} WR</p>
                              <p><strong>⏰ Time Remaining:</strong> {(8 - careerData.workSession.progress).toFixed(1)} hours</p>
                            </div>
                          </div>
                        ) : careerData.userJob.jobLevel !== 'fieldAgent' && !careerData.workSession ? (
                          <div className="work-actions">
                            <button
                              className="start-work-btn"
                              onClick={handleStartWork}
                              disabled={careerLoading}
                            >
                              {careerLoading ? '⏳ Starting...' : '🚀 Start Work Shift'}
                            </button>
                            <p className="work-requirement-note">
                              Must be within 5km of office location to start work. Work shifts are only available Monday through Thursday.
                            </p>
                          </div>
                        ) : null}
                        
                        {/* Field Operation Status for Field Agents */}
                        {careerData.userJob.jobLevel === 'fieldAgent' && (
                          <div className="field-operation-status">
                            {careerData.fieldOperation ? (
                              <div className="field-operation-active">
                                <h6>🔄 Field Operations Active</h6>
                                <div className="operation-progress">
                                  <div className="progress-bar">
                                    <div 
                                      className="progress-fill"
                                      style={{ width: `${(careerData.fieldOperation.progress / 8) * 100}%` }}
                                    ></div>
                                  </div>
                                  <p>{careerData.fieldOperation.hoursWorked} / 8.0 hours on duty</p>
                                  <p><strong>👮 Arrests Made:</strong> {careerData.fieldOperation.arrestsMade || 0}</p>
                                  <p><strong>📍 Status:</strong> On Active Duty</p>
                                </div>
                              </div>
                            ) : (
                              <div className="field-operation-info">
                                <h6>🤵 Field Agent Status</h6>
                                <p>✅ Ready for field operations</p>
                                <p>📋 Move to your office location and click the office icon to start field operations</p>
                                <p>🔄 Field agents automatically track weekly streak progress while on duty</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Quit Job Button */}
                        <div className="job-actions">
                          <button
                            className="quit-job-btn"
                            onClick={handleQuitJob}
                            disabled={careerLoading}
                          >
                            {careerLoading ? '⏳ Processing...' : '📋 Hand in Notice & Quit Job'}
                          </button>
                          <p className="job-action-note">
                            You can apply for higher positions after quitting
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                    {/* Application Status Section */}
                    <div className="application-status-section">
                      {careerData.applicationStatus && !careerData.applicationStatus.canApply && (
                        <>
                          {careerData.applicationStatus.hasPendingApplication && (
                            <div className="pending-application">
                              <h4>⏳ Application Under Review</h4>
                              <div className="application-card pending">
                                <div className="application-header">
                                  <h5>📋 {careerData.applicationStatus.application.jobTitle}</h5>
                                  <span className="status-badge pending">Under Review</span>
                                </div>
                                <div className="application-details">
                                  <p><strong>🏢 Company:</strong> {careerData.applicationStatus.application.company}</p>
                                  <p><strong>📍 Location:</strong> {careerData.applicationStatus.application.officeName}</p>
                                  <p><strong>📅 Applied:</strong> {new Date(careerData.applicationStatus.application.appliedAt).toLocaleDateString()}</p>
                                  <p><strong>⏰ Decision in:</strong> {careerData.applicationStatus.timeRemaining}</p>
                                </div>
                                <div className="review-info">
                                  <p className="review-info-text">
                                    📊 Your application is being carefully reviewed. You will receive a decision within 48 hours.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {careerData.applicationStatus.hasRejectionCooldown && (
                            <div className="rejection-cooldown">
                              <h4>📝 Application Cooldown</h4>
                              <div className="cooldown-card">
                                <div className="cooldown-message">
                                  <p className="rejection-message">
                                    📬 After careful consideration, we have decided not to take your application any further. 
                                    Please apply in the future.
                                  </p>
                                  <p className="cooldown-timer"><strong>⏳ Next application allowed in:</strong> {careerData.applicationStatus.timeRemaining}</p>
                                  <p className="cooldown-date">
                                    Available again: {new Date(careerData.applicationStatus.nextApplicationAllowed).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Job Applications */}
                    <div className="job-applications-section">
                      <h4>🌍 Available Positions</h4>
                      <p>Apply for cybersecurity positions at global companies. Choose from two career levels:</p>
                      
                      <div className="job-levels">
                        <div className="job-level-card">
                          <h5>🔰 Junior Network Security Officer</h5>
                          <p><strong>Requirements:</strong> 1,500 XP</p>
                          <p><strong>Salary:</strong> 18 WR/hour</p>
                          <p><strong>Schedule:</strong> Monday-Thursday, 8-hour shifts</p>
                        </div>
                        <div className="job-level-card">
                          <h5>⭐ Network Security Administrator</h5>
                          <p><strong>Requirements:</strong> 2,000 XP</p>
                          <p><strong>Salary:</strong> 28 WR/hour</p>
                          <p><strong>Schedule:</strong> Monday-Thursday, 8-hour shifts</p>
                        </div>
                        <div className="job-level-card field-agent-card">
                          <h5>🤵 Field Agent</h5>
                          <p><strong>Requirements:</strong> 5,000 XP + 3 months as Senior</p>
                          <p><strong>Salary:</strong> 35 WR/hour</p>
                          <p><strong>Schedule:</strong> Monday-Thursday, field operations</p>
                          <p className="special-abilities">
                            ⭐ Special Abilities: Arrest powers
                          </p>
                        </div>
                      </div>

                      {/* Office Locations */}
                      <div className="office-locations">
                        <h5>🏢 Office Locations</h5>
                        <div className="offices-grid">
                          {careerData.offices && careerData.offices.map((office) => (
                            <div key={office._id} className="office-card">
                              <div className="office-header">
                                <h6>🏢 {office.officeName}</h6>
                                <p className="office-company">{office.company}</p>
                              </div>
                              <div className="office-details">
                                <p><strong>📍 Location:</strong> {office.city}, {office.country}</p>
                                <p><strong>🌍 Coordinates:</strong> {office.location.lat.toFixed(4)}, {office.location.lng.toFixed(4)}</p>
                              </div>
                              
                              <div className="application-buttons">
                                <button
                                  onClick={() => handleJobApplication(office._id, 'junior')}
                                  disabled={careerLoading || (user?.xp || 0) < 1500 || !canApplyForJobs()}
                                  title={!canApplyForJobs() ? getApplicationDisabledReason() : ''}
                                  style={{
                                    background: (user?.xp || 0) >= 1500 && canApplyForJobs() ? '#007bff' : '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 8px',
                                    borderRadius: '3px',
                                    cursor: (user?.xp || 0) >= 1500 && canApplyForJobs() ? 'pointer' : 'not-allowed',
                                    fontSize: '11px',
                                    margin: '1px',
                                    width: '32%'
                                  }}
                                >
                                  🔰 Junior
                                </button>
                                <button
                                  onClick={() => handleJobApplication(office._id, 'senior')}
                                  disabled={careerLoading || (user?.xp || 0) < 2000 || !canApplyForJobs()}
                                  title={!canApplyForJobs() ? getApplicationDisabledReason() : ''}
                                  style={{
                                    background: (user?.xp || 0) >= 2000 && canApplyForJobs() ? '#28a745' : '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 8px',
                                    borderRadius: '3px',
                                    cursor: (user?.xp || 0) >= 2000 && canApplyForJobs() ? 'pointer' : 'not-allowed',
                                    fontSize: '11px',
                                    margin: '1px',
                                    width: '32%'
                                  }}
                                >
                                  ⭐ Senior
                                </button>
                                <button
                                  onClick={() => handleJobApplication(office._id, 'fieldAgent')}
                                  disabled={careerLoading || (user?.xp || 0) < 5000 || !canApplyForJobs()}
                                  title={!canApplyForJobs() ? getApplicationDisabledReason() : (user?.xp || 0) < 5000 ? 'Requires 5,000 XP and 3 months as Senior' : ''}
                                  style={{
                                    background: (user?.xp || 0) >= 5000 && canApplyForJobs() ? '#ffd700' : '#6c757d',
                                    color: (user?.xp || 0) >= 5000 && canApplyForJobs() ? '#000' : 'white',
                                    border: 'none',
                                    padding: '8px 8px',
                                    borderRadius: '3px',
                                    cursor: (user?.xp || 0) >= 5000 && canApplyForJobs() ? 'pointer' : 'not-allowed',
                                    fontSize: '11px',
                                    margin: '1px',
                                    width: '32%'
                                  }}
                                >
                                  🤵 Agent
                                </button>
                              </div>
                              
                              {(user?.xp || 0) < 1500 && (
                                <p style={{ fontSize: '10px', color: '#dc3545', marginTop: '5px' }}>
                                  Need {1500 - (user?.xp || 0)} more XP for Junior position
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                    </>
                  )}

                  {/* Weekly Streak Display - Shows for all users regardless of job status */}
                  <div className="weekly-streak-section">
                    <h4>🔥 Weekly Streak Progress</h4>
                    {streakLoading ? (
                      <div className="loading-state">
                        <p>🔄 Loading streak data...</p>
                      </div>
                    ) : streakData ? (
                      <div className="streak-display">
                        <div className="current-week-progress">
                          <div className="week-info">
                            <span className="streak-title">This Week ({streakData.currentWeekDays}/4 days)</span>
                            {streakData.currentStreak > 0 && (
                              <span className="streak-level">Streak Level: {streakData.currentStreak}</span>
                            )}
                          </div>
                          <div className="daily-progress">
                            {[1, 2, 3, 4].map(day => (
                              <div 
                                key={day} 
                                className={`day-indicator ${day <= streakData.currentWeekDays ? 'completed' : 'pending'}`}
                                title={`Day ${day} ${day <= streakData.currentWeekDays ? 'Completed' : 'Pending'}`}
                              >
                                {day <= streakData.currentWeekDays ? '✅' : '⭕'}
                              </div>
                            ))}
                          </div>
                          <div className="bonus-info">
                            {streakData.currentWeekDays === 4 ? (
                              <span className="bonus-earned">🎉 Week Complete! Bonus earned!</span>
                            ) : streakData.hasActiveJob === false ? (
                              <span className="next-bonus" style={{color: 'var(--hacker-green)'}}>
                                🎯 Apply for a job to start earning {streakData.nextBonusAmount} WR coin bonuses!
                              </span>
                            ) : (
                              <span className="next-bonus">
                                Next bonus: {streakData.nextBonusAmount} WR coins
                                {streakData.currentWeekDays < 4 && ` (${4 - streakData.currentWeekDays} more days)`}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {streakData.streakHistory && streakData.streakHistory.length > 0 && (
                          <div className="streak-history">
                            <p className="history-title">Recent Streak History:</p>
                            <div className="history-items">
                              {streakData.streakHistory.slice(-3).map((week, index) => (
                                <div key={index} className="history-item">
                                  <span className="week-date">
                                    {new Date(week.weekStartDate).toLocaleDateString()}
                                  </span>
                                  <span className="week-details">
                                    {week.daysWorked} days, Level {week.streakLevel}, +{week.bonusAmount} WR
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="streak-unavailable">
                        <p>📊 Weekly Streak Bonus System</p>
                        <p>🎯 <strong>Earn escalating bonuses for consistent work:</strong></p>
                        <ul style={{textAlign: 'left', margin: '10px 0', paddingLeft: '20px'}}>
                          <li>Week 1: Complete 4 days → Earn 50 WR coins</li>
                          <li>Week 2: Complete 4 days → Earn 75 WR coins</li>
                          <li>Week 3: Complete 4 days → Earn 100 WR coins</li>
                          <li>Week 4+: Complete 4 days → Earn 125 WR coins</li>
                        </ul>
                        <p style={{color: 'var(--hacker-green)', fontWeight: 'bold'}}>💼 Apply for a job above to start earning streak bonuses!</p>
                      </div>
                    )}
                  </div>

                  {/* Career Information */}
                  <div className="career-info">
                    <h4>ℹ️ Career Information</h4>
                    <div className="info-grid">
                      <div className="info-card">
                        <h6>📋 Job Requirements</h6>
                        <ul>
                          <li>Must be within 5km of office to work</li>
                          <li>Work Monday-Thursday only</li>
                          <li>8-hour shifts with automatic payment</li>
                          <li>Miss 4+ consecutive days = termination</li>
                        </ul>
                      </div>
                      <div className="info-card">
                        <h6>💰 Payment System</h6>
                        <ul>
                          <li>Hourly payment: Junior (18 WR) / Senior (28 WR)</li>
                          <li>Automatic payment after shift completion</li>
                          <li>Real-time progress tracking</li>
                          <li>Attendance bonuses for consistency</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedHotel && (
        <div className="modal-overlay">
          <div className="modal-content booking-modal">
            <div className="modal-header">
              <h3>Book Room at {selectedHotel.name}</h3>
              <button 
                className="close-button"
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedHotel(null);
                  setBookingNights(1);
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="hotel-details">
                <p><strong>📍 Location:</strong> {selectedHotel.country}</p>
                <p><strong>⭐ Rating:</strong> {getStarRating(selectedHotel.rating)}</p>
                <p><strong>💰 Price:</strong> {selectedHotel.pricePerNight} WR/night</p>
                <p><strong>🏨 Available Rooms:</strong> {selectedHotel.availableRooms}</p>
              </div>
              
              <div className="booking-form">
                <div className="form-group">
                  <label htmlFor="nights">Number of Nights (1-14):</label>
                  <input
                    type="number"
                    id="nights"
                    min="1"
                    max="14"
                    value={bookingNights}
                    onChange={(e) => setBookingNights(parseInt(e.target.value) || 1)}
                    className="nights-input"
                  />
                </div>
                
                <div className="cost-summary">
                  <p><strong>Total Cost: {selectedHotel.pricePerNight * bookingNights} WR</strong></p>
                  <p>Your Balance: {user?.wrCoins || 0} WR</p>
                  <p>Remaining: {(user?.wrCoins || 0) - (selectedHotel.pricePerNight * bookingNights)} WR</p>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button
                className="confirm-booking-button"
                onClick={handleBookHotel}
                disabled={bookingLoading || (user?.wrCoins || 0) < (selectedHotel.pricePerNight * bookingNights)}
              >
                {bookingLoading ? 'Booking...' : 'Confirm Booking'}
              </button>
              <button
                className="cancel-button"
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedHotel(null);
                  setBookingNights(1);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shopping;
